import { createI18n } from "vue-i18n";

import { accessMessages } from "./access-messages";
import { agentMessages } from "./agent-messages";
import {
  communicationMessages,
  task10Messages,
} from "./communication-messages";
import { followUpMessages, task11Messages } from "./follow-up-messages";
import { organizationMessages } from "./organization-messages";
import { queryMessages } from "./query-messages";
import { queryFlowMessages } from "./query-flow-messages";
import { commercialTaxRuleMessages } from "./commercial-tax-rule-messages";

const itineraryMessages = {
  en: {
    actions: {
      retry: "Retry",
      retryLocations: "Retry locations",
      retryLookups: "Retry lookups",
      create: "Create itinerary",
      creating: "Creating…",
      addCity: "Add city",
      cloneRevision: "Clone revision",
      createRevision: "Create revision",
      removeCity: "Remove city",
      addItem: "Add item",
      removeItem: "Remove item",
      cancel: "Cancel",
      save: "Save",
      saving: "Saving…",
      yes: "Yes",
      no: "No",
    },
    fields: {
      location: "Location",
      numberOfDays: "Number of days",
      title: "Title",
      description: "Description",
      type: "Type",
      hotel: "Hotel",
      roomType: "Room type",
      supplier: "Supplier",
      serviceMaster: "Service",
      directEntry: "Custom / direct entry",
      none: "None",
      name: "Name",
      quantity: "Quantity",
      startTime: "Start time",
      endTime: "End time",
      internalNotes: "Internal notes",
    },
    serviceTypes: {
      HOTEL: "Hotel",
      TRANSPORTATION: "Transportation",
      ACTIVITY: "Activity",
      GUIDE: "Guide",
      TICKET: "Ticket",
      OTHER: "Other",
    },
    versionTypes: {
      DRAFT: "Draft",
      PRICED_REVISION: "Priced revision",
      CONFIRMED: "Confirmed",
      AMENDMENT: "Amendment",
      OPERATIONAL_AMENDMENT: "Operational amendment",
    },
    version: {
      number: "Version {number}",
      history: "Version history",
      current: "Current",
      pendingReviewAndSend: "Pending review & send",
      historical: "Historical",
      confirmedBaseline: "Confirmed baseline",
      option: "Version {number} · {type} · {state}",
    },
    coverage: {
      planned: "{planned} / {total} days planned",
      remaining: "{count} day remaining | {count} days remaining",
      complete: "Complete",
      incomplete: "Incomplete",
      completeCoverage: "Complete coverage",
    },
    empty: {
      title: "Build this itinerary from scratch",
      description:
        "Add ordered city stops to cover the inclusive trip from {start} to {end}. Templates and package copying are not used.",
      noCities: "No city stops yet",
      addFirstCity: "Add the first city to generate globally numbered days.",
    },
    states: {
      loading: "Loading itinerary",
      loadingVersion: "Loading itinerary version",
      createRestricted:
        "You have view access but not permission to create an itinerary.",
      bookingLocked: "Booking Locked",
      readOnly: "Read-only itinerary",
      pendingReviewAndSend: "Pending review & send",
      pendingReviewAndSendDescription:
        "This Reservation revision is ready to review and send; the accepted itinerary remains current until the revised delivery succeeds.",
      historical: "Historical version",
      lockedDescription:
        "The confirmed booking baseline cannot be edited directly. Later changes use Amendments.",
      readOnlyDescription:
        "You can view this itinerary, but direct editing is not available.",
      historicalDescription:
        "Historical versions are immutable and remain available for reference.",
    },
    stop: {
      addTitle: "Add city stop",
      editTitle: "Edit {city}",
      countryHelp:
        "Choose an active location in this Query's destination country.",
      noLocations: "No active locations match your search.",
      cardLabel: "{city} city stop",
      moveUpLabel: "Move {city} up",
      moveDownLabel: "Move {city} down",
      editLabel: "Edit {city}",
      actionsLabel: "Actions for {city}",
    },
    day: {
      cardLabel: "{city}, Day {day}",
      toggleLabel: "Expand or collapse {city}, Day {day}",
      numberDate: "Day {day} · {date}",
      datePending: "Date pending",
      itemCount: "{count} item | {count} items",
      editLabel: "Edit {city}, Day {day}",
      editTitle: "Edit {city}, Day {day}",
      noItems: "No items yet.",
      noDays: "No days",
      singleRange: "Day {first}",
      range: "Days {first}–{last}",
      count: "{count} day | {count} days",
    },
    service: {
      addTitle: "Add item to {city}, Day {day}",
      editTitle: "Edit item in {city}, Day {day}",
      addLabel: "Add item to {city}, Day {day}",
      editLabel: "Edit {name} in {city}, Day {day}",
      moveUpLabel: "Move {name} up in {city}, Day {day}",
      moveDownLabel: "Move {name} down in {city}, Day {day}",
      actionsLabel: "Actions for {name} in {city}, Day {day}",
      quantityValue: "Qty {quantity}",
      queryTravellerDefaults:
        "Using Query traveller defaults. Adjusting these values creates an itinerary override without changing the Query.",
      participantOverride:
        "Itinerary participant override. The Query traveller record is unchanged.",
    },
    hotelStay: {
      guests: "Guests",
      adults: "{count} adult | {count} adults",
      children: "{count} child | {count} children",
      ages: "ages {ages}",
      supplementsTitle: "Property supplements",
      supplementsHelp:
        "Property-wide add-ons only. Commercial add-ons appear during costing.",
      supplementsLoading: "Loading property supplements…",
      supplementsError:
        "Supplements could not be loaded. Hotel, room and date selections are kept.",
      supplementsRetry: "Retry supplements",
      supplementsRevalidating: "Revalidating supplements…",
      mandatoryApplied: "Applied automatically",
      staleRemoved:
        "{count} previously selected add-on(s) are no longer eligible and were removed.",
      validity: "Valid {from} → {to}",
    },
    validation: {
      locationRequired: "Select an active location from the list.",
      daysPositive: "Number of days must be a positive whole number.",
      titleRequired: "Title is required.",
      nameRequired: "Name is required.",
      quantityPositive: "Quantity must be a positive integer.",
    },
    errors: {
      versionHistory: "The itinerary version history could not be loaded.",
      version: "This itinerary version could not be loaded.",
      locations:
        "Locations could not be loaded. Your itinerary and entered values are unchanged.",
      locationPartial:
        "Active location choices could not be loaded. The itinerary remains available.",
      serviceLookups:
        "Some Hotel, Supplier or Service choices could not be loaded. You can keep direct-entry values.",
      roomTypes: "Room types could not be loaded.",
      durationExceeded:
        "The city duration would exceed the Query trip duration.",
      locationCountryMismatch:
        "That location does not belong to this Query destination country.",
      masterInactive:
        "A selected master record is no longer active. Refresh the choices and select another.",
      notEditable: "This itinerary is no longer editable.",
      locked: "The confirmed booking is locked. Changes must use an Amendment.",
      updateFailed:
        "The itinerary could not be updated. Your entered values were kept.",
    },
    success: {
      created: "Itinerary created. Add the first city stop.",
      versionCreated: "Version {version} created.",
      cityAdded: "City stop added.",
      cityUpdated: "City stop updated.",
      cityOrderUpdated: "City order updated.",
      cityRemoved: "City stop removed.",
      dayUpdated: "Day updated.",
      itemSaved: "Item saved.",
      itemOrderUpdated: "Item order updated.",
      itemRemoved: "Item removed.",
    },
    confirm: {
      title: "Confirm itinerary change",
      removeCityDescription:
        "Remove {city} and {range}? Its days and entered content will be soft-removed.",
      resizeDescription:
        "Resize {city} and remove {range}? Entered day details and service rows in that range will be soft-removed.",
      removeItemDescription:
        "Remove {name} from {city}, Day {day}? The historical row will be retained.",
    },
    delivery: {
      title: "Review & Send itinerary",
      documentMode: "Document mode",
      itineraryOnly: "Itinerary only",
      priced: "Priced itinerary",
      presentation: "Price presentation",
      presentationTotal: "Total",
      presentationPerPerson: "Per person",
      presentationServiceLevel: "Service level",
      validUntil: "Valid until (optional)",
      subtotal: "Subtotal",
      tax: "Tax",
      total: "Total",
      pricedNotReady:
        "Complete and approve the exact itinerary costing before sending a priced itinerary.",
      pricedChanged:
        "The itinerary, costing, tax, or reviewed total changed. Refresh and review again.",
      taxNotConfigured: "No commercial tax rule applies to this issue date.",
      taxAmbiguous:
        "More than one commercial tax rule applies to this issue date.",
      loading: "Loading delivery details…",
      draftFailed: "Delivery details could not be loaded.",
      from: "From",
      to: "To",
      subject: "Subject",
      body: "Message",
      freezeNotice:
        "Sending permanently locks itinerary version {version}. Future changes require a new revision.",
      summary: "Version {version} will be attached as {file}.",
      queued: "Itinerary queued for delivery.",
      versionConflict:
        "The itinerary changed before it could be queued. Your entered values were kept.",
      conversationChanged:
        "The Query conversation changed before it could be queued. Refresh and review the email again.",
      continuesConversation:
        "This itinerary will be sent in the existing Query conversation. Its subject is kept unchanged.",
      coverageIncomplete: "Complete every trip day before sending.",
      agentEmailRequired: "Add a valid Agent email before sending.",
      mailboxUnavailable: "The Reservation Executive mailbox is unavailable.",
      alreadyExists: "This itinerary version already has a delivery.",
      queryLocked: "This Query is locked and cannot be sent.",
      sendFailed:
        "The itinerary could not be queued. Your entered values were kept.",
      sending: "Queueing…",
      send: "Send itinerary",
      locked: "Delivery Locked",
      lockedDescription:
        "This delivered itinerary version is immutable. Create a revision to make later changes.",
      reviewSend: "Review & Send",
      reviewSendForVersion: "Review and send itinerary version {version}",
      manualZeroSellingWarning: "Manual item(s) with a selling price of 0: {items}.",
      retry: "Retry delivery",
      retryForVersion: "Retry delivery for itinerary version {version}",
      retryQueued: "Delivery queued for another attempt.",
      failed: "Delivery failed ({code}).",
      unknownFailure: "unknown error",
      unknownSentAt: "an unknown time",
      sent: "Version {version} sent from {sender} to {recipient} at {sentAt}.",
      progress: "The secure delivery worker is processing this message.",
      contentPurged: "The temporary PDF content has been purged.",
      contentPurgedValue: "content purged",
      status: {
        NOT_SENT: "Not sent",
        PREPARING: "Preparing PDF",
        QUEUED: "Queued",
        SENDING: "Sending",
        SENT: "Sent",
        FAILED: "Failed",
      },
    },
    decision: {
      title: "Record Agent decision",
      acceptTitle: "Accept delivered priced itinerary",
      description:
        "Record explicit final commercial acceptance with a Hold expiry, or record a rejection with retained evidence and a reason.",
      query: "Query",
      itineraryVersion: "Itinerary version",
      acceptanceChannel: "Acceptance channel",
      selectAcceptanceChannel: "Select acceptance channel",
      otherChannel: "Other",
      holdExpiryDate: "Hold expiry date",
      holdExpiryHelp: "Valid through this date in the organization timezone.",
      holdExpiryTimezoneHelp: "Valid through this date in {timezone}",
      loadingExpiryPreview: "Checking the authoritative expiry…",
      expiryPreview: "Backend-derived expiry instant: {expiresAt}",
      expiryInvalid: "Choose a date whose organization-timezone expiry is still in the future.",
      referenceNote: "Acceptance reference note (optional)",
      acceptanceEvidence: "Acceptance evidence (optional)",
      acceptanceEvidenceHelp: "PDF, PNG or JPEG proof retained against this exact delivered version.",
      acceptanceAttestation: "The client accepts this quotation; proceed.",
      loadingAcceptance: "Loading the exact acceptance context…",
      acceptanceDraftError: "The acceptance context could not be loaded. Refresh it before accepting.",
      refreshReviewRequired: "The Query or priced itinerary changed. The latest state was refreshed; review it before accepting again.",
      rejectionEvidence: "Rejection evidence",
      expiryRequired: "Select the Hold expiry date.",
      rejectionReason: "Decision note / rejection reason",
      proofRequired:
        "Attach Agent-decision proof or enter a decision note before recording.",
      reasonRequired: "Enter the Agent rejection reason.",
      accept: "Block",
      accepting: "Blocking…",
      reject: "Requote",
      accepted: "Quote blocked.",
      rejected: "Quote marked for requote.",
      recorded: "{decision} by {actor} at {recordedAt}.",
      failed: "The Agent decision could not be recorded.",
    },
    externalDelivery: {
      title: "Record external delivery",
      description:
        "Use the exact retained PDF and attach evidence for a delivery completed outside the configured provider.",
      downloadExactPdf: "Download exact retained PDF",
      downloadFailed: "The retained PDF could not be downloaded.",
      channel: "Delivery channel",
      sentAt: "Actual sent time",
      record: "Record external delivery",
      recording: "Recording…",
      recorded: "External delivery recorded.",
      verified: "Externally delivered by {channel} at {sentAt}.",
      proofRequired: "Attach proof and enter the channel and actual sent time.",
      timeInvalid: "Actual sent time must be a valid time in the past.",
      failed: "External delivery could not be recorded.",
    },
    conflict: {
      title: "A newer itinerary exists",
      description:
        "Another change was saved after this version loaded. Your dialog values are still here. Refresh the itinerary before trying again.",
      review: "Review entered values",
      refresh: "Refresh itinerary",
    },
  },
  ja: {
    actions: {
      retry: "再試行",
      retryLocations: "ロケーションを再読込",
      retryLookups: "選択肢を再読込",
      create: "旅程を作成",
      creating: "作成中…",
      addCity: "都市を追加",
      cloneRevision: "改訂版を複製",
      createRevision: "改訂版を作成",
      removeCity: "都市を削除",
      addItem: "項目を追加",
      removeItem: "項目を削除",
      cancel: "キャンセル",
      save: "保存",
      saving: "保存中…",
      yes: "はい",
      no: "いいえ",
    },
    fields: {
      location: "ロケーション",
      numberOfDays: "日数",
      title: "タイトル",
      description: "説明",
      type: "種類",
      hotel: "ホテル",
      roomType: "客室タイプ",
      supplier: "サプライヤー",
      serviceMaster: "サービス",
      directEntry: "カスタム／直接入力",
      none: "なし",
      name: "名前",
      quantity: "数量",
      startTime: "開始時刻",
      endTime: "終了時刻",
      internalNotes: "内部メモ",
    },
    serviceTypes: {
      HOTEL: "ホテル",
      TRANSPORTATION: "交通",
      ACTIVITY: "アクティビティ",
      GUIDE: "ガイド",
      TICKET: "チケット",
      RESTAURANT: "レストラン",
      OTHER: "その他",
    },
    versionTypes: {
      DRAFT: "下書き",
      PRICED_REVISION: "価格付き改訂",
      CONFIRMED: "確定",
      AMENDMENT: "変更",
      OPERATIONAL_AMENDMENT: "運用変更",
    },
    version: {
      number: "バージョン {number}",
      history: "バージョン履歴",
      current: "現在",
      pendingReviewAndSend: "確認・送信待ち",
      historical: "履歴",
      confirmedBaseline: "確定基準",
      option: "バージョン {number}・{type}・{state}",
    },
    coverage: {
      planned: "{planned} / {total} 日を計画済み",
      remaining: "残り {count} 日",
      complete: "完了",
      incomplete: "未完了",
      completeCoverage: "全日程完了",
    },
    empty: {
      title: "旅程を一から作成",
      description:
        "{start}～{end} の旅行期間を、順序付きの都市滞在で構成します。テンプレートやパッケージコピーは使用しません。",
      noCities: "都市滞在はまだありません",
      addFirstCity:
        "最初の都市を追加すると、全体通し番号の日程が生成されます。",
    },
    states: {
      loading: "旅程を読み込み中",
      loadingVersion: "旅程バージョンを読み込み中",
      createRestricted:
        "閲覧権限はありますが、旅程を作成する権限がありません。",
      bookingLocked: "予約ロック済み",
      readOnly: "読み取り専用旅程",
      pendingReviewAndSend: "確認・送信待ち",
      pendingReviewAndSendDescription:
        "この予約改訂は確認して送信できます。改訂版の配信が成功するまで、承認済み旅程が現在の版のままです。",
      historical: "履歴バージョン",
      lockedDescription:
        "確定済み予約基準は直接編集できません。以後の変更は変更申請を使用します。",
      readOnlyDescription: "旅程を閲覧できますが、直接編集はできません。",
      historicalDescription:
        "履歴バージョンは変更不可で、参照用に保持されます。",
    },
    stop: {
      addTitle: "都市滞在を追加",
      editTitle: "{city}を編集",
      countryHelp: "このクエリの目的国内にある有効なロケーションを選択します。",
      noLocations: "検索に一致する有効なロケーションがありません。",
      cardLabel: "{city}の都市滞在",
      moveUpLabel: "{city}を上へ移動",
      moveDownLabel: "{city}を下へ移動",
      editLabel: "{city}を編集",
      actionsLabel: "{city}の操作",
    },
    day: {
      cardLabel: "{city}、{day}日目",
      toggleLabel: "{city}の{day}日目を展開または折りたたむ",
      numberDate: "{day}日目・{date}",
      datePending: "日付未定",
      itemCount: "{count} 件",
      editLabel: "{city}の{day}日目を編集",
      editTitle: "{city}の{day}日目を編集",
      noItems: "項目はまだありません。",
      noDays: "日程なし",
      singleRange: "{first}日目",
      range: "{first}～{last}日目",
      count: "{count} 日",
    },
    service: {
      addTitle: "{city}の{day}日目に項目を追加",
      editTitle: "{city}の{day}日目の項目を編集",
      addLabel: "{city}の{day}日目に項目を追加",
      editLabel: "{city}の{day}日目の{name}を編集",
      moveUpLabel: "{city}の{day}日目の{name}を上へ移動",
      moveDownLabel: "{city}の{day}日目の{name}を下へ移動",
      actionsLabel: "{city}の{day}日目の{name}の操作",
      quantityValue: "数量 {quantity}",
      queryTravellerDefaults:
        "クエリの旅行者既定値を使用しています。ここで調整すると、クエリを変更せず旅程の上書き値になります。",
      participantOverride:
        "旅程の参加者上書き値です。クエリの旅行者情報は変更されません。",
    },
    hotelStay: {
      guests: "宿泊者",
      adults: "大人 {count} 名",
      children: "子ども {count} 名",
      ages: "年齢 {ages}",
      supplementsTitle: "プロパティ追加オプション",
      supplementsHelp:
        "プロパティ共通の追加オプションのみです。契約専用の追加オプションはコスト計算で表示されます。",
      supplementsLoading: "プロパティ追加オプションを読み込み中…",
      supplementsError:
        "追加オプションを読み込めませんでした。ホテル・客室・日付の選択は保持されています。",
      supplementsRetry: "追加オプションを再試行",
      supplementsRevalidating: "追加オプションを再確認中…",
      mandatoryApplied: "自動適用",
      staleRemoved: "対象外となった {count} 件の選択を解除しました。",
      validity: "有効期間 {from} → {to}",
    },
    validation: {
      locationRequired: "一覧から有効なロケーションを選択してください。",
      daysPositive: "日数は1以上の整数で入力してください。",
      titleRequired: "タイトルは必須です。",
      nameRequired: "名前は必須です。",
      quantityPositive: "数量は正の数で入力してください。",
    },
    errors: {
      versionHistory: "旅程のバージョン履歴を読み込めませんでした。",
      version: "この旅程バージョンを読み込めませんでした。",
      locations:
        "ロケーションを読み込めませんでした。旅程と入力内容は保持されています。",
      locationPartial:
        "有効なロケーション候補を読み込めませんでした。旅程は引き続き表示できます。",
      serviceLookups:
        "ホテル、サプライヤー、サービスの一部を読み込めませんでした。直接入力の値は保持できます。",
      roomTypes: "客室タイプを読み込めませんでした。",
      durationExceeded: "都市の滞在日数がクエリの旅行日数を超えます。",
      locationCountryMismatch:
        "そのロケーションはクエリの目的国に属していません。",
      masterInactive:
        "選択したマスターレコードは無効です。候補を更新して選び直してください。",
      notEditable: "この旅程は編集できません。",
      locked: "確定済み予約はロックされています。変更申請を使用してください。",
      updateFailed: "旅程を更新できませんでした。入力内容は保持されています。",
    },
    success: {
      created: "旅程を作成しました。最初の都市を追加してください。",
      versionCreated: "バージョン {version} を作成しました。",
      cityAdded: "都市滞在を追加しました。",
      cityUpdated: "都市滞在を更新しました。",
      cityOrderUpdated: "都市順を更新しました。",
      cityRemoved: "都市滞在を削除しました。",
      dayUpdated: "日程を更新しました。",
      itemSaved: "項目を保存しました。",
      itemOrderUpdated: "項目順を更新しました。",
      itemRemoved: "項目を削除しました。",
    },
    confirm: {
      title: "旅程変更の確認",
      removeCityDescription:
        "{city}と{range}を削除しますか？日程と入力内容は論理削除されます。",
      resizeDescription:
        "{city}を短縮して{range}を削除しますか？範囲内の日程詳細とサービス行は論理削除されます。",
      removeItemDescription:
        "{city}の{day}日目から{name}を削除しますか？履歴行は保持されます。",
    },
    delivery: {
      title: "旅程を確認して送信",
      loading: "送信情報を読み込み中…",
      draftFailed: "送信情報を読み込めませんでした。",
      from: "差出人",
      to: "宛先",
      subject: "件名",
      body: "メッセージ",
      freezeNotice:
        "送信すると旅程バージョン {version} は永久にロックされます。以後の変更には新しい改訂版が必要です。",
      summary: "バージョン {version} を {file} として添付します。",
      queued: "旅程を送信キューに追加しました。",
      versionConflict:
        "キュー追加前に旅程が変更されました。入力値は保持されています。",
      conversationChanged:
        "キュー追加前にクエリの会話が変更されました。更新してメールを再確認してください。",
      continuesConversation:
        "この旅程は既存のクエリ会話で送信されます。件名は変更されません。",
      coverageIncomplete: "送信前にすべての旅行日を完成してください。",
      agentEmailRequired:
        "送信前に有効なエージェントメールを追加してください。",
      mailboxUnavailable: "予約担当者のメールボックスを利用できません。",
      alreadyExists: "この旅程バージョンには既に送信履歴があります。",
      queryLocked: "このクエリはロックされているため送信できません。",
      sendFailed:
        "旅程を送信キューに追加できませんでした。入力値は保持されています。",
      sending: "キュー追加中…",
      send: "旅程を送信",
      locked: "送信ロック済み",
      lockedDescription:
        "送信済み旅程バージョンは変更できません。変更するには改訂版を作成してください。",
      reviewSend: "確認して送信",
      reviewSendForVersion: "旅程バージョン {version} を確認して送信",
      retry: "送信を再試行",
      retryForVersion: "旅程バージョン {version} の送信を再試行",
      retryQueued: "送信を再試行キューに追加しました。",
      failed: "送信に失敗しました（{code}）。",
      unknownFailure: "不明なエラー",
      sent: "バージョン {version} は {sender} から {recipient} に {sentAt} 送信されました。",
      progress: "安全な送信ワーカーがメッセージを処理しています。",
      contentPurged: "一時 PDF データは削除済みです。",
      contentPurgedValue: "削除済み",
      status: {
        NOT_SENT: "未送信",
        PREPARING: "PDF 作成中",
        QUEUED: "待機中",
        SENDING: "送信中",
        SENT: "送信済み",
        FAILED: "失敗",
      },
    },
    conflict: {
      title: "新しい旅程があります",
      description:
        "このバージョンの読み込み後に別の変更が保存されました。ダイアログの入力値は保持されています。再試行前に旅程を更新してください。",
      review: "入力値を確認",
      refresh: "旅程を更新",
    },
  },
  "zh-TW": {
    actions: {
      retry: "重試",
      retryLocations: "重試地點",
      retryLookups: "重試選項",
      create: "建立行程",
      creating: "建立中…",
      addCity: "新增城市",
      cloneRevision: "複製修訂版",
      createRevision: "建立修訂版",
      removeCity: "移除城市",
      addItem: "新增項目",
      removeItem: "移除項目",
      cancel: "取消",
      save: "儲存",
      saving: "儲存中…",
      yes: "是",
      no: "否",
    },
    fields: {
      location: "地點",
      numberOfDays: "天數",
      title: "標題",
      description: "說明",
      type: "類型",
      hotel: "飯店",
      roomType: "房型",
      supplier: "供應商",
      serviceMaster: "服務",
      directEntry: "自訂／直接輸入",
      none: "無",
      name: "名稱",
      quantity: "數量",
      startTime: "開始時間",
      endTime: "結束時間",
      internalNotes: "內部備註",
    },
    serviceTypes: {
      HOTEL: "飯店",
      TRANSPORTATION: "交通",
      ACTIVITY: "活動",
      GUIDE: "導遊",
      TICKET: "票券",
      RESTAURANT: "餐廳",
      OTHER: "其他",
    },
    versionTypes: {
      DRAFT: "草稿",
      PRICED_REVISION: "定價修訂",
      CONFIRMED: "已確認",
      AMENDMENT: "修訂",
      OPERATIONAL_AMENDMENT: "營運修訂",
    },
    version: {
      number: "版本 {number}",
      history: "版本紀錄",
      current: "目前",
      pendingReviewAndSend: "等待檢閱並傳送",
      historical: "歷史",
      confirmedBaseline: "已確認基準",
      option: "版本 {number}・{type}・{state}",
    },
    coverage: {
      planned: "已規劃 {planned} / {total} 天",
      remaining: "剩餘 {count} 天",
      complete: "完整",
      incomplete: "未完整",
      completeCoverage: "涵蓋完整",
    },
    empty: {
      title: "從零開始建立行程",
      description:
        "新增依序排列的城市停留，以涵蓋 {start} 至 {end} 的完整旅程。不使用範本或套裝複製。",
      noCities: "尚無城市停留",
      addFirstCity: "新增第一個城市以產生全程連續編號的每日行程。",
    },
    states: {
      loading: "正在載入行程",
      loadingVersion: "正在載入行程版本",
      createRestricted: "您可以檢視，但沒有建立行程的權限。",
      bookingLocked: "預訂已鎖定",
      readOnly: "唯讀行程",
      pendingReviewAndSend: "等待檢閱並傳送",
      pendingReviewAndSendDescription:
        "此預訂修訂已可檢閱並傳送；在修訂版送達成功前，已接受的行程仍為目前版本。",
      historical: "歷史版本",
      lockedDescription: "已確認的預訂基準無法直接編輯；後續變更須使用修訂。",
      readOnlyDescription: "您可以檢視此行程，但無法直接編輯。",
      historicalDescription: "歷史版本不可變更，並保留供查閱。",
    },
    stop: {
      addTitle: "新增城市停留",
      editTitle: "編輯 {city}",
      countryHelp: "選擇此查詢目的地國家中啟用的地點。",
      noLocations: "沒有符合搜尋的啟用地點。",
      cardLabel: "{city} 城市停留",
      moveUpLabel: "將 {city} 上移",
      moveDownLabel: "將 {city} 下移",
      editLabel: "編輯 {city}",
      actionsLabel: "{city} 的操作",
    },
    day: {
      cardLabel: "{city}，第 {day} 天",
      toggleLabel: "展開或收合 {city} 第 {day} 天",
      numberDate: "第 {day} 天・{date}",
      datePending: "日期待定",
      itemCount: "{count} 個項目",
      editLabel: "編輯 {city} 第 {day} 天",
      editTitle: "編輯 {city} 第 {day} 天",
      noItems: "尚無項目。",
      noDays: "無天數",
      singleRange: "第 {first} 天",
      range: "第 {first}–{last} 天",
      count: "{count} 天",
    },
    service: {
      addTitle: "新增項目至 {city} 第 {day} 天",
      editTitle: "編輯 {city} 第 {day} 天的項目",
      addLabel: "新增項目至 {city} 第 {day} 天",
      editLabel: "編輯 {city} 第 {day} 天的 {name}",
      moveUpLabel: "將 {city} 第 {day} 天的 {name} 上移",
      moveDownLabel: "將 {city} 第 {day} 天的 {name} 下移",
      actionsLabel: "{city} 第 {day} 天之 {name} 的操作",
      quantityValue: "數量 {quantity}",
      queryTravellerDefaults:
        "目前使用查詢旅客預設值。在此調整會建立行程覆寫值，不會變更查詢。",
      participantOverride: "行程參加者覆寫值。查詢旅客記錄未變更。",
    },
    hotelStay: {
      guests: "入住旅客",
      adults: "{count} 位成人",
      children: "{count} 位兒童",
      ages: "年齡 {ages}",
      supplementsTitle: "飯店附加選項",
      supplementsHelp:
        "僅限飯店通用的附加選項。合約專屬附加選項會在成本計算時顯示。",
      supplementsLoading: "正在載入飯店附加選項…",
      supplementsError: "無法載入附加選項。飯店、房型與日期選擇已保留。",
      supplementsRetry: "重試附加選項",
      supplementsRevalidating: "正在重新驗證附加選項…",
      mandatoryApplied: "自動套用",
      staleRemoved: "已有 {count} 個先前選取的項目因不再符合資格而移除。",
      validity: "有效期 {from} → {to}",
    },
    validation: {
      locationRequired: "請從清單選擇啟用的地點。",
      daysPositive: "天數必須是正整數。",
      titleRequired: "標題為必填。",
      nameRequired: "名稱為必填。",
      quantityPositive: "數量必須大於零。",
    },
    errors: {
      versionHistory: "無法載入行程版本紀錄。",
      version: "無法載入此行程版本。",
      locations: "無法載入地點；行程與輸入值未變更。",
      locationPartial: "無法載入啟用的地點選項；行程仍可使用。",
      serviceLookups:
        "部分飯店、供應商或服務選項無法載入；您可以保留直接輸入值。",
      roomTypes: "無法載入房型。",
      durationExceeded: "城市天數將超過查詢的旅程天數。",
      locationCountryMismatch: "該地點不屬於此查詢的目的地國家。",
      masterInactive: "選取的主資料已停用；請重新整理選項後再選擇。",
      notEditable: "此行程已無法編輯。",
      locked: "已確認的預訂已鎖定；變更須使用修訂。",
      updateFailed: "無法更新行程；您的輸入值已保留。",
    },
    success: {
      created: "已建立行程。請新增第一個城市。",
      versionCreated: "已建立版本 {version}。",
      cityAdded: "已新增城市停留。",
      cityUpdated: "已更新城市停留。",
      cityOrderUpdated: "已更新城市順序。",
      cityRemoved: "已移除城市停留。",
      dayUpdated: "已更新每日行程。",
      itemSaved: "已儲存項目。",
      itemOrderUpdated: "已更新項目順序。",
      itemRemoved: "已移除項目。",
    },
    confirm: {
      title: "確認行程變更",
      removeCityDescription:
        "移除 {city} 與{range}嗎？其每日行程與輸入內容將被軟移除。",
      resizeDescription:
        "縮短 {city} 並移除{range}嗎？範圍內的每日詳細資料與服務列將被軟移除。",
      removeItemDescription:
        "從 {city} 第 {day} 天移除 {name} 嗎？歷史資料列將保留。",
    },
    delivery: {
      title: "檢視並傳送行程",
      loading: "正在載入傳送資料…",
      draftFailed: "無法載入傳送資料。",
      from: "寄件者",
      to: "收件者",
      subject: "主旨",
      body: "訊息",
      freezeNotice:
        "傳送後將永久鎖定行程版本 {version}；後續變更必須建立新修訂版。",
      summary: "版本 {version} 將以 {file} 附加。",
      queued: "行程已排入傳送佇列。",
      versionConflict: "排入佇列前行程已有變更；輸入內容已保留。",
      conversationChanged:
        "排入佇列前查詢對話已有變更，請重新整理並再次檢視郵件。",
      continuesConversation: "此行程將在現有查詢對話中傳送，主旨會保持不變。",
      coverageIncomplete: "請先完成所有行程日期再傳送。",
      agentEmailRequired: "請先新增有效的代理商電子郵件。",
      mailboxUnavailable: "無法使用訂房專員信箱。",
      alreadyExists: "此行程版本已有傳送記錄。",
      queryLocked: "此查詢已鎖定，無法傳送。",
      sendFailed: "無法將行程排入傳送佇列；輸入內容已保留。",
      sending: "正在排入佇列…",
      send: "傳送行程",
      locked: "傳送後鎖定",
      lockedDescription:
        "此已傳送的行程版本不可變更；請建立修訂版進行後續修改。",
      reviewSend: "檢視並傳送",
      reviewSendForVersion: "檢視並傳送行程版本 {version}",
      retry: "重試傳送",
      retryForVersion: "重試傳送行程版本 {version}",
      retryQueued: "已排入再次傳送佇列。",
      failed: "傳送失敗（{code}）。",
      unknownFailure: "未知錯誤",
      sent: "版本 {version} 已於 {sentAt} 由 {sender} 傳送至 {recipient}。",
      progress: "安全傳送背景程序正在處理此訊息。",
      contentPurged: "暫存 PDF 內容已清除。",
      contentPurgedValue: "內容已清除",
      status: {
        NOT_SENT: "未傳送",
        PREPARING: "正在建立 PDF",
        QUEUED: "已排入佇列",
        SENDING: "傳送中",
        SENT: "已傳送",
        FAILED: "失敗",
      },
    },
    conflict: {
      title: "已有較新的行程",
      description:
        "載入此版本後已有其他變更儲存。對話框輸入值仍保留；重試前請重新整理行程。",
      review: "檢視輸入值",
      refresh: "重新整理行程",
    },
  },
  ko: {
    actions: {
      retry: "다시 시도",
      retryLocations: "위치 다시 시도",
      retryLookups: "선택 항목 다시 시도",
      create: "일정 만들기",
      creating: "만드는 중…",
      addCity: "도시 추가",
      cloneRevision: "개정본 복제",
      createRevision: "개정본 만들기",
      removeCity: "도시 제거",
      addItem: "항목 추가",
      removeItem: "항목 제거",
      cancel: "취소",
      save: "저장",
      saving: "저장 중…",
      yes: "예",
      no: "아니요",
    },
    fields: {
      location: "위치",
      numberOfDays: "일수",
      title: "제목",
      description: "설명",
      type: "유형",
      hotel: "호텔",
      roomType: "객실 유형",
      supplier: "공급업체",
      serviceMaster: "서비스",
      directEntry: "사용자 지정／직접 입력",
      none: "없음",
      name: "이름",
      quantity: "수량",
      startTime: "시작 시간",
      endTime: "종료 시간",
      internalNotes: "내부 메모",
    },
    serviceTypes: {
      HOTEL: "호텔",
      TRANSPORTATION: "교통",
      ACTIVITY: "활동",
      GUIDE: "가이드",
      TICKET: "티켓",
      RESTAURANT: "레스토랑",
      OTHER: "기타",
    },
    versionTypes: {
      DRAFT: "초안",
      PRICED_REVISION: "가격 포함 개정",
      CONFIRMED: "확정",
      AMENDMENT: "변경",
      OPERATIONAL_AMENDMENT: "운영 변경",
    },
    version: {
      number: "버전 {number}",
      history: "버전 기록",
      current: "현재",
      pendingReviewAndSend: "검토 및 전송 대기",
      historical: "과거",
      confirmedBaseline: "확정 기준",
      option: "버전 {number}・{type}・{state}",
    },
    coverage: {
      planned: "{planned} / {total}일 계획됨",
      remaining: "{count}일 남음",
      complete: "완료",
      incomplete: "미완료",
      completeCoverage: "전체 일정 완료",
    },
    empty: {
      title: "일정을 처음부터 만들기",
      description:
        "{start}부터 {end}까지의 전체 여행을 순서가 지정된 도시 체류로 구성합니다. 템플릿이나 패키지 복사는 사용하지 않습니다.",
      noCities: "아직 도시 체류가 없습니다",
      addFirstCity:
        "첫 도시를 추가하면 전체 일정에 걸친 일자 번호가 생성됩니다.",
    },
    states: {
      loading: "일정 불러오는 중",
      loadingVersion: "일정 버전 불러오는 중",
      createRestricted: "조회 권한은 있지만 일정을 만들 권한이 없습니다.",
      bookingLocked: "예약 잠김",
      readOnly: "읽기 전용 일정",
      pendingReviewAndSend: "검토 및 전송 대기",
      pendingReviewAndSendDescription:
        "이 예약 개정본은 검토 후 전송할 수 있습니다. 개정본 전송이 성공할 때까지 승인된 일정이 현재 버전으로 유지됩니다.",
      historical: "과거 버전",
      lockedDescription:
        "확정된 예약 기준은 직접 편집할 수 없습니다. 이후 변경은 변경 요청을 사용합니다.",
      readOnlyDescription: "이 일정을 볼 수 있지만 직접 편집할 수 없습니다.",
      historicalDescription:
        "과거 버전은 변경할 수 없으며 참조용으로 유지됩니다.",
    },
    stop: {
      addTitle: "도시 체류 추가",
      editTitle: "{city} 편집",
      countryHelp: "이 쿼리의 목적지 국가에서 활성 위치를 선택하세요.",
      noLocations: "검색과 일치하는 활성 위치가 없습니다.",
      cardLabel: "{city} 도시 체류",
      moveUpLabel: "{city} 위로 이동",
      moveDownLabel: "{city} 아래로 이동",
      editLabel: "{city} 편집",
      actionsLabel: "{city} 작업",
    },
    day: {
      cardLabel: "{city}, {day}일차",
      toggleLabel: "{city} {day}일차 펼치기 또는 접기",
      numberDate: "{day}일차・{date}",
      datePending: "날짜 미정",
      itemCount: "{count}개 항목",
      editLabel: "{city} {day}일차 편집",
      editTitle: "{city} {day}일차 편집",
      noItems: "아직 항목이 없습니다.",
      noDays: "일자 없음",
      singleRange: "{first}일차",
      range: "{first}–{last}일차",
      count: "{count}일",
    },
    service: {
      addTitle: "{city} {day}일차에 항목 추가",
      editTitle: "{city} {day}일차 항목 편집",
      addLabel: "{city} {day}일차에 항목 추가",
      editLabel: "{city} {day}일차의 {name} 편집",
      moveUpLabel: "{city} {day}일차의 {name} 위로 이동",
      moveDownLabel: "{city} {day}일차의 {name} 아래로 이동",
      actionsLabel: "{city} {day}일차의 {name} 작업",
      quantityValue: "수량 {quantity}",
      queryTravellerDefaults:
        "쿼리 여행자 기본값을 사용합니다. 여기서 조정하면 쿼리를 변경하지 않고 일정 재정의 값이 생성됩니다.",
      participantOverride:
        "일정 참가자 재정의 값입니다. 쿼리 여행자 기록은 변경되지 않습니다.",
    },
    hotelStay: {
      guests: "투숙객",
      adults: "성인 {count}명",
      children: "아동 {count}명",
      ages: "나이 {ages}",
      supplementsTitle: "호텔 추가 옵션",
      supplementsHelp:
        "호텔 공용 추가 옵션만 표시됩니다. 계약 전용 추가 옵션은 비용 산정에서 표시됩니다.",
      supplementsLoading: "호텔 추가 옵션을 불러오는 중…",
      supplementsError:
        "추가 옵션을 불러올 수 없습니다. 호텔·객실·날짜 선택은 유지됩니다.",
      supplementsRetry: "추가 옵션 다시 시도",
      supplementsRevalidating: "추가 옵션을 다시 확인하는 중…",
      mandatoryApplied: "자동 적용",
      staleRemoved: "더 이상 해당하지 않는 {count}개 선택이 제거되었습니다.",
      validity: "유효 기간 {from} → {to}",
    },
    validation: {
      locationRequired: "목록에서 활성 위치를 선택하세요.",
      daysPositive: "일수는 양의 정수여야 합니다.",
      titleRequired: "제목은 필수입니다.",
      nameRequired: "이름은 필수입니다.",
      quantityPositive: "수량은 양수여야 합니다.",
    },
    errors: {
      versionHistory: "일정 버전 기록을 불러올 수 없습니다.",
      version: "이 일정 버전을 불러올 수 없습니다.",
      locations:
        "위치를 불러올 수 없습니다. 일정과 입력값은 변경되지 않았습니다.",
      locationPartial:
        "활성 위치 선택 항목을 불러올 수 없습니다. 일정은 계속 사용할 수 있습니다.",
      serviceLookups:
        "일부 호텔, 공급업체 또는 서비스 선택 항목을 불러올 수 없습니다. 직접 입력값은 유지할 수 있습니다.",
      roomTypes: "객실 유형을 불러올 수 없습니다.",
      durationExceeded: "도시 체류 기간이 쿼리 여행 기간을 초과합니다.",
      locationCountryMismatch:
        "해당 위치는 이 쿼리의 목적지 국가에 속하지 않습니다.",
      masterInactive:
        "선택한 마스터 레코드가 비활성 상태입니다. 선택 항목을 새로 고치고 다시 선택하세요.",
      notEditable: "이 일정은 더 이상 편집할 수 없습니다.",
      locked: "확정된 예약이 잠겼습니다. 변경 요청을 사용해야 합니다.",
      updateFailed: "일정을 업데이트할 수 없습니다. 입력값은 유지되었습니다.",
    },
    success: {
      created: "일정을 만들었습니다. 첫 도시를 추가하세요.",
      versionCreated: "버전 {version}을 만들었습니다.",
      cityAdded: "도시 체류를 추가했습니다.",
      cityUpdated: "도시 체류를 업데이트했습니다.",
      cityOrderUpdated: "도시 순서를 업데이트했습니다.",
      cityRemoved: "도시 체류를 제거했습니다.",
      dayUpdated: "일자를 업데이트했습니다.",
      itemSaved: "항목을 저장했습니다.",
      itemOrderUpdated: "항목 순서를 업데이트했습니다.",
      itemRemoved: "항목을 제거했습니다.",
    },
    confirm: {
      title: "일정 변경 확인",
      removeCityDescription:
        "{city} 및 {range}을(를) 제거하시겠습니까? 일자와 입력 내용은 소프트 제거됩니다.",
      resizeDescription:
        "{city}의 기간을 줄이고 {range}을(를) 제거하시겠습니까? 해당 범위의 일자 상세 정보와 서비스 행은 소프트 제거됩니다.",
      removeItemDescription:
        "{city} {day}일차에서 {name}을(를) 제거하시겠습니까? 과거 행은 유지됩니다.",
    },
    delivery: {
      title: "일정 검토 및 보내기",
      loading: "전송 정보를 불러오는 중…",
      draftFailed: "전송 정보를 불러올 수 없습니다.",
      from: "보낸 사람",
      to: "받는 사람",
      subject: "제목",
      body: "메시지",
      freezeNotice:
        "보내면 일정 버전 {version}이 영구 잠깁니다. 이후 변경은 새 개정본이 필요합니다.",
      summary: "버전 {version}이 {file}(으)로 첨부됩니다.",
      queued: "일정이 전송 대기열에 추가되었습니다.",
      versionConflict:
        "대기열에 추가하기 전에 일정이 변경되었습니다. 입력값은 유지되었습니다.",
      conversationChanged:
        "대기열에 추가하기 전에 쿼리 대화가 변경되었습니다. 새로 고친 후 이메일을 다시 검토하세요.",
      continuesConversation:
        "이 일정은 기존 쿼리 대화에서 전송되며 제목은 변경되지 않습니다.",
      coverageIncomplete: "보내기 전에 모든 여행 일정을 완성하세요.",
      agentEmailRequired: "보내기 전에 유효한 에이전트 이메일을 추가하세요.",
      mailboxUnavailable: "예약 담당자 사서함을 사용할 수 없습니다.",
      alreadyExists: "이 일정 버전에는 이미 전송 기록이 있습니다.",
      queryLocked: "이 쿼리는 잠겨 있어 보낼 수 없습니다.",
      sendFailed:
        "일정을 전송 대기열에 추가할 수 없습니다. 입력값은 유지되었습니다.",
      sending: "대기열에 추가 중…",
      send: "일정 보내기",
      locked: "전송 잠김",
      lockedDescription:
        "전송된 일정 버전은 변경할 수 없습니다. 이후 변경은 개정본을 만드세요.",
      reviewSend: "검토 및 보내기",
      reviewSendForVersion: "일정 버전 {version} 검토 및 보내기",
      retry: "전송 다시 시도",
      retryForVersion: "일정 버전 {version} 전송 다시 시도",
      retryQueued: "전송이 다시 대기열에 추가되었습니다.",
      failed: "전송 실패({code}).",
      unknownFailure: "알 수 없는 오류",
      sent: "버전 {version}을(를) {sentAt}에 {sender}에서 {recipient}(으)로 보냈습니다.",
      progress: "보안 전송 작업자가 메시지를 처리하고 있습니다.",
      contentPurged: "임시 PDF 콘텐츠가 삭제되었습니다.",
      contentPurgedValue: "콘텐츠 삭제됨",
      status: {
        NOT_SENT: "보내지 않음",
        PREPARING: "PDF 준비 중",
        QUEUED: "대기 중",
        SENDING: "보내는 중",
        SENT: "보냄",
        FAILED: "실패",
      },
    },
    conflict: {
      title: "더 최신 일정이 있습니다",
      description:
        "이 버전을 불러온 뒤 다른 변경 사항이 저장되었습니다. 대화상자의 입력값은 유지됩니다. 다시 시도하기 전에 일정을 새로 고치세요.",
      review: "입력값 검토",
      refresh: "일정 새로 고침",
    },
  },
} as const;

const travelMasterMessages = {
  en: {
    common: {
      add: "Add",
      name: "Name",
      status: "Status",
      actions: "Actions",
      active: "Active",
      inactive: "Inactive",
      activate: "Activate",
      deactivate: "Deactivate",
      allStatuses: "All statuses",
      allCountries: "All countries",
      search: "Search",
      loading: "Loading…",
      loadError: "The list could not be loaded.",
      saveError:
        "The record could not be saved. Your entered values were kept.",
      empty: "No matching records.",
      noPermission: "You do not have permission to view this master.",
      retry: "Retry",
      previous: "Previous",
      next: "Next",
      cancel: "Cancel",
      save: "Save",
      refresh: "Refresh latest",
      selectCountry: "Select country",
      country: "Country",
      description: "Description",
      confirmDeactivation: "Confirm deactivation",
      deactivationDescription:
        "Deactivate {name}? Historical references will remain available.",
      yesDeactivate: "Yes, deactivate",
      no: "No",
      close: "Close",
      readOnly: "You can view this record but cannot edit it.",
      lookupFailed: "Some choices could not be loaded.",
      retrySuppliers: "Retry suppliers",
      retryCountries: "Retry countries",
      refreshKeepEntries: "Reload latest and keep my entries",
    },
    fields: {
      timezone: "Timezone",
      customFields: "Custom fields",
      category: "Category",
      address: "Address",
      contactInformation: "Contact information",
      location: "Location",
      supplier: "Vendor",
      notes: "Notes",
      roomType: "Room type",
      allCategories: "All categories",
      allSuppliers: "All vendors",
      noSupplier: "No vendor",
      noLocation: "No location",
    },
    entities: {
      location: "Location",
      locations: "In-country locations",
      locationsDescription: "Manage active destinations within a country.",
      hotel: "Hotel",
      hotels: "Hotels",
      hotelsDescription: "Manage destination hotels and room types.",
      supplier: "Vendor",
      suppliers: "Vendors",
      suppliersDescription: "Manage destination vendors.",
      service: "Service",
      services: "Services",
      servicesDescription: "Manage reusable destination services.",
      roomType: "Room type",
      roomTypes: "Room types",
      roomTypesDescription: "Managed within this Hotel.",
    },
    messages: {
      created: "{entity} created.",
      updated: "{entity} updated.",
      saved: "{entity} saved.",
      activated: "{entity} activated.",
      deactivated: "{entity} deactivated.",
      saveFailed: "The {entity} could not be saved.",
      statusFailed: "The {entity} status could not be changed.",
      versionConflict:
        "Another user changed this record. Your entries were kept. Reload the latest version and review before retrying.",
    },
    hotelEditor: {
      tabs: {
        overview: "Overview",
        roomTypes: "Room Types",
        rateCards: "Rate Cards",
      },      context: {
        newHotel: "New Hotel",
        location: "Location",
        country: "Country",
        status: "Status",
      },
      continueTitle: "Hotel saved. Continue where you need to go next.",
      continueRoomTypes: "Continue to Room Types",
      continueRateCards: "Continue to Rate Cards",
      rateCardsRestricted:
        "You have Hotel access but not rate-card view permission, so commercial rate details are hidden.",
      supplierAssociationNote:
        "Suppliers below are derived from this Hotel’s existing rate cards only; they do not represent general supplier contracts.",
      openRateCards: "Open in Hotel rate cards",
      backFromCards: "Rate-card changes are saved on the card itself.",
    },
  },
  ja: {
    common: {
      add: "追加",
      name: "名前",
      status: "状態",
      actions: "操作",
      active: "有効",
      inactive: "無効",
      activate: "有効化",
      deactivate: "無効化",
      allStatuses: "すべての状態",
      allCountries: "すべての国",
      search: "検索",
      loading: "読み込み中…",
      loadError: "一覧を読み込めませんでした。",
      saveError: "レコードを保存できませんでした。入力内容は保持されています。",
      empty: "一致するレコードがありません。",
      noPermission: "このマスターを表示する権限がありません。",
      retry: "再試行",
      previous: "前へ",
      next: "次へ",
      cancel: "キャンセル",
      save: "保存",
      refresh: "最新版を更新",
      selectCountry: "国を選択",
      country: "国",
      description: "説明",
      confirmDeactivation: "無効化の確認",
      deactivationDescription:
        "{name}を無効化しますか？履歴参照は保持されます。",
      yesDeactivate: "はい、無効化",
      no: "いいえ",
    },
    fields: {
      timezone: "タイムゾーン",
      customFields: "カスタム項目",
      category: "カテゴリー",
      address: "住所",
      contactInformation: "連絡先情報",
      location: "ロケーション",
      supplier: "サプライヤー",
      supplierType: "サプライヤー種類",
      notes: "メモ",
      roomType: "客室タイプ",
      allCategories: "すべてのカテゴリー",
      allSuppliers: "すべてのサプライヤー",
      noSupplier: "サプライヤーなし",
      noLocation: "ロケーションなし",
    },
    entities: {
      location: "ロケーション",
      locations: "国内ロケーション",
      locationsDescription: "国内の有効な目的地を管理します。",
      hotel: "ホテル",
      hotels: "ホテル",
      hotelsDescription: "目的地のホテルと客室タイプを管理します。",
      supplier: "サプライヤー",
      suppliers: "サプライヤー",
      suppliersDescription: "目的地のサプライヤーを管理します。",
      service: "サービス",
      services: "サービス",
      servicesDescription: "再利用可能な目的地サービスを管理します。",
      roomType: "客室タイプ",
      roomTypes: "客室タイプ",
      roomTypesDescription: "このホテル内で管理します。",
    },
    messages: {
      created: "{entity}を作成しました。",
      updated: "{entity}を更新しました。",
      saved: "{entity}を保存しました。",
      activated: "{entity}を有効化しました。",
      deactivated: "{entity}を無効化しました。",
      saveFailed: "{entity}を保存できませんでした。",
      statusFailed: "{entity}の状態を変更できませんでした。",
    },
  },
  "zh-TW": {
    common: {
      add: "新增",
      name: "名稱",
      status: "狀態",
      actions: "操作",
      active: "啟用",
      inactive: "停用",
      activate: "啟用",
      deactivate: "停用",
      allStatuses: "所有狀態",
      allCountries: "所有國家",
      search: "搜尋",
      loading: "載入中…",
      loadError: "無法載入清單。",
      saveError: "無法儲存記錄；您輸入的值已保留。",
      empty: "沒有符合的記錄。",
      noPermission: "您沒有檢視此主資料的權限。",
      retry: "重試",
      previous: "上一頁",
      next: "下一頁",
      cancel: "取消",
      save: "儲存",
      refresh: "重新整理最新資料",
      selectCountry: "選擇國家",
      country: "國家",
      description: "說明",
      confirmDeactivation: "確認停用",
      deactivationDescription: "停用 {name} 嗎？歷史參照仍會保留。",
      yesDeactivate: "是，停用",
      no: "否",
    },
    fields: {
      timezone: "時區",
      customFields: "自訂欄位",
      category: "類別",
      address: "地址",
      contactInformation: "聯絡資訊",
      location: "地點",
      supplier: "供應商",
      supplierType: "供應商類型",
      notes: "備註",
      roomType: "房型",
      allCategories: "所有類別",
      allSuppliers: "所有供應商",
      noSupplier: "無供應商",
      noLocation: "無地點",
    },
    entities: {
      location: "地點",
      locations: "境內地點",
      locationsDescription: "管理國家內啟用的目的地。",
      hotel: "飯店",
      hotels: "飯店",
      hotelsDescription: "管理目的地飯店與房型。",
      supplier: "供應商",
      suppliers: "供應商",
      suppliersDescription: "管理目的地供應商。",
      service: "服務",
      services: "服務",
      servicesDescription: "管理可重複使用的目的地服務。",
      roomType: "房型",
      roomTypes: "房型",
      roomTypesDescription: "在此飯店內管理。",
    },
    messages: {
      created: "已建立{entity}。",
      updated: "已更新{entity}。",
      saved: "已儲存{entity}。",
      activated: "已啟用{entity}。",
      deactivated: "已停用{entity}。",
      saveFailed: "無法儲存{entity}。",
      statusFailed: "無法變更{entity}的狀態。",
    },
  },
  ko: {
    common: {
      add: "추가",
      name: "이름",
      status: "상태",
      actions: "작업",
      active: "활성",
      inactive: "비활성",
      activate: "활성화",
      deactivate: "비활성화",
      allStatuses: "모든 상태",
      allCountries: "모든 국가",
      search: "검색",
      loading: "불러오는 중…",
      loadError: "목록을 불러올 수 없습니다.",
      saveError: "레코드를 저장할 수 없습니다. 입력한 값은 유지되었습니다.",
      empty: "일치하는 레코드가 없습니다.",
      noPermission: "이 마스터를 볼 권한이 없습니다.",
      retry: "다시 시도",
      previous: "이전",
      next: "다음",
      cancel: "취소",
      save: "저장",
      refresh: "최신 정보 새로 고침",
      selectCountry: "국가 선택",
      country: "국가",
      description: "설명",
      confirmDeactivation: "비활성화 확인",
      deactivationDescription:
        "{name}을(를) 비활성화하시겠습니까? 과거 참조는 유지됩니다.",
      yesDeactivate: "예, 비활성화",
      no: "아니요",
    },
    fields: {
      timezone: "시간대",
      customFields: "사용자 지정 필드",
      category: "범주",
      address: "주소",
      contactInformation: "연락처 정보",
      location: "위치",
      supplier: "공급업체",
      supplierType: "공급업체 유형",
      notes: "메모",
      roomType: "객실 유형",
      allCategories: "모든 범주",
      allSuppliers: "모든 공급업체",
      noSupplier: "공급업체 없음",
      noLocation: "위치 없음",
    },
    entities: {
      location: "위치",
      locations: "국내 위치",
      locationsDescription: "국가 내 활성 목적지를 관리합니다.",
      hotel: "호텔",
      hotels: "호텔",
      hotelsDescription: "목적지 호텔과 객실 유형을 관리합니다.",
      supplier: "공급업체",
      suppliers: "공급업체",
      suppliersDescription: "목적지 공급업체를 관리합니다.",
      service: "서비스",
      services: "서비스",
      servicesDescription: "재사용 가능한 목적지 서비스를 관리합니다.",
      roomType: "객실 유형",
      roomTypes: "객실 유형",
      roomTypesDescription: "이 호텔 내에서 관리합니다.",
    },
    messages: {
      created: "{entity}을(를) 생성했습니다.",
      updated: "{entity}을(를) 업데이트했습니다.",
      saved: "{entity}을(를) 저장했습니다.",
      activated: "{entity}을(를) 활성화했습니다.",
      deactivated: "{entity}을(를) 비활성화했습니다.",
      saveFailed: "{entity}을(를) 저장할 수 없습니다.",
      statusFailed: "{entity} 상태를 변경할 수 없습니다.",
    },
  },
} as const;

const supplierTypeMessages = {
  en: {
    HOTEL: "Hotel",
    TRANSPORT: "Transport",
    GUIDE: "Guide",
    ACTIVITY: "Activity",
    TICKET: "Ticket",
    RESTAURANT: "Restaurant",
    OTHER: "Other",
  },
  ja: {
    HOTEL: "ホテル",
    TRANSPORT: "運送",
    GUIDE: "ガイド",
    ACTIVITY: "アクティビティ",
    TICKET: "チケット",
    RESTAURANT: "レストラン",
    OTHER: "その他",
  },
  "zh-TW": {
    HOTEL: "飯店",
    TRANSPORT: "運輸",
    GUIDE: "導遊",
    ACTIVITY: "活動",
    TICKET: "票券",
    RESTAURANT: "餐廳",
    OTHER: "其他",
  },
  ko: {
    HOTEL: "호텔",
    TRANSPORT: "운송",
    GUIDE: "가이드",
    ACTIVITY: "활동",
    TICKET: "티켓",
    RESTAURANT: "레스토랑",
    OTHER: "기타",
  },
} as const;

const queryWorkspaceMessages = {
  en: {
    header: {
      label: "Query workspace",
      tripNumber: "Trip Number",
      bookingLocked: "Booking Locked",
      destination: "Destination",
      agent: "Agent",
      travelDates: "Travel dates",
      travellers: "Travellers",
      preferences: "Preferences",
      bookingType: "Booking type",
      workflowStage: "Workflow stage",
      lifecycleOutcome: "Lifecycle outcome",
    },
    back: "Back",
    mainStatuses: {
      LIVE: "Live",
      HOLD: "Hold",
      CONVERTED: "Converted",
      COLD: "Cold",
    },
    lifecycleStatuses: {
      ACTIVE: "Active",
      LOST: "Lost",
      CANCELLED: "Cancelled",
      CLOSED: "Closed",
    },
    bookingTypes: { FIT: "FIT", GROUP: "Group" },
    stages: {
      RESERVATION_ACTIVE: "Itinerary building",
      QUOTATION_SENT: "Priced itinerary sent",
      PRICED_ITINERARY_SENT: "Priced itinerary sent",
      AGENT_ACCEPTED: "Agent accepted",
      READY_TO_INVOICE: "Ready to Invoice",
      AWAITING_FINANCE_GATE_1: "Awaiting Finance Gate 1",
      OPERATIONS_ACTIVE: "Operations active",
      AWAITING_FINANCE_GATE_2: "Awaiting Finance Gate 2",
      QC_ACTIVE: "QC active",
      TRAVEL_PACK_READY: "Travel Pack ready",
      SUPPORT_ACTIVE: "Support active",
      TRIP_COMPLETED: "Trip completed",
      AWAITING_DEPARTMENT_CLOSURE: "Awaiting department closure",
      COLD: "Cold",
      CLOSED: "Closed",
    },
    displayStages: {
      ACKNOWLEDGEMENT_PENDING: "Acknowledgement pending",
      ACKNOWLEDGED: "Acknowledged",
      ITINERARY_BUILDING: "Itinerary building",
      QUOTED_REQUOTED: "Quoted / Requoted",
      FOLLOW_UP: "Follow Up",
      ON_HOLD: "On Hold / Availability Check",
      READY_TO_INVOICE: "Ready to Invoice",
      OPERATIONS: "Operations",
      FINANCE_APPROVED: "Finance Approved",
      FINAL_QC: "Final QC",
      TRAVEL_HANDOVER: "Travel Handover",
      ON_GROUND: "On Ground",
      AMENDMENT: "Amendment",
      DEPARTMENT_CHECKS: "Department Checks",
      CLOSED: "Closed",
      COLD: "Cold",
    },
    tabs: {
      overview: "Workflow stage",
      communication: "Communication",
      itinerary: "Itinerary",
      quotations: "Quotations",
      costing: "Costing",
      travellers: "Travellers",
      finance: "Finance",
      operations: "Operations",
      qc: "QC",
      "travel-pack": "Travel Pack",
      support: "Support",
      activity: "Activity",
    },
    directLinkRestricted:
      "You do not have permission to open the {tab} workspace for this Query. The Overview remains available.",
    overview: {
      title: "Workflow stage",
      queryDetails: "Query details",
      contact: "Agent contact",
      currency: "Currency",
      nights: "Nights",
      primaryTraveller: "Primary traveller",
      adults: "Adults",
      children: "Children",
      age: "Age",
      ages: "Ages",
      focTravellers: "FOC travellers",
    },
    communication: {
      delegatedSender:
        "Replies and itinerary emails are sent from {executive}’s assigned mailbox. Your action is recorded in the audit history.",
    },
    hold: {
      title: "Current Hold",
      description:
        "Authoritative acceptance and Hold timing for the accepted priced itinerary.",
      loading: "Loading Hold details…",
      error: "The Hold summary could not be loaded.",
      partialErrorTitle: "The Hold could not be refreshed",
      partialErrorDescription:
        "The last safe Hold details remain visible. Retry to check the latest server state.",
      empty:
        "This Query has no current Hold. Historical Hold cycles remain available below when present.",
      acceptedVersion: "Accepted itinerary version",
      acceptedDelivery: "Accepted delivery",
      acceptedBy: "Accepted by",
      acceptedAt: "Accepted at",
      channel: "Acceptance channel",
      enteredExpiry: "Entered expiry date and timezone",
      utcExpiry: "Server-derived UTC expiry",
      localExpiry: "Operating-timezone expiry",
      timeRemaining: "Time remaining",
      referenceNote: "Acceptance note",
      history: "Historical Holds",
      invoiceExternalNotice:
        "Invoicing happens outside CRM. No invoice generation, preview, download or Send actions are available here.",
      states: {
        active: "Active",
        expired: "Expired",
        superseded: "Superseded",
        converted: "Converted / historical",
        historical: "Historical",
      },
      remainingDays: "{days}d {hours}h remaining",
      remainingHours: "{hours}h {minutes}m remaining",
      remainingMinutes: "{minutes}m remaining",
      remainingLessThanMinute: "Less than 1 minute remaining",
      boundaryReached: "Expiry boundary reached",
      boundaryRefresh:
        "The shown expiry boundary has been reached. Refreshing server state; the browser does not expire the Hold.",
      extend: "Extend Hold",
      extendTitle: "Extend current Hold",
      extendDescription:
        "Enter a later expiry date and reason. The server derives the authoritative expiry instant in the organization timezone.",
      newExpiryDate: "New expiry date",
      extensionReason: "Reason",
      extending: "Extending…",
      extendSuccess: "Hold extended.",
      extendFailed: "The Hold could not be extended.",
      extendRefreshReview:
        "The Query or Hold changed. The latest Hold was refreshed; review it before submitting again.",
    },
    availability: {
      title: "Availability review",
      description:
        "Hotel and Ground decisions for the exact itinerary version accepted into this Hold.",
      loading: "Loading availability reviews…",
      error: "The availability reviews could not be loaded.",
      partialErrorTitle: "Some availability details could not be refreshed",
      partialErrorDescription:
        "The last safe availability data remains visible. Retry before making a decision that depends on the missing details.",
      empty: "There is no current Hold availability review.",
      historical: "Historical",
      historicalReadOnly:
        "This expired or superseded Hold cycle is historical and read-only.",
      cycleStates: {
        ACTIVE: "Active",
        EXPIRED: "Expired",
        SUPERSEDED: "Superseded",
        CONVERTED: "Converted",
      },
      teams: {
        HOTEL: "Hotel Operations",
        GROUND: "Ground Operations",
      },
      outcomes: {
        PENDING: "Pending",
        AVAILABLE: "Available",
        ALTERNATIVE_REQUIRED: "Alternative required",
        NOT_REQUIRED: "Not required",
      },
      applicability: "Applicability",
      applicable: "Applicable",
      notApplicable: "Not applicable",
      unassignedTeam: "No assigned team",
      acceptedVersion: "Exact accepted version",
      reviewer: "Reviewer",
      timestamp: "Decision timestamp",
      notes: "Review notes",
      reason: "Reason",
      notRecorded: "Not recorded",
      owner: "Action owner",
      status: "Status",
      nextAction: "Next action",
      routing: {
        operationsResolved:
          "Hotel availability is confirmed. This order-only change can be sent by Ground Operations.",
        reservations:
          "Hotel availability is unresolved. Reservations will combine this request with other itinerary changes.",
        none: "No further owner or action is available.",
      },
      nextActions: {
        reservationsRevision: "Reservations prepares the revised itinerary",
        reviewAndSend: "Review and send the created revision",
        operationsResequence: "Operations creates a sequence-only revision",
        handOff: "Hand off the proposed change to Reservations",
        none: "No further action",
      },
      markAvailable: "Mark Available",
      proposeAlternative: "Propose alternative",
      reviewItinerary: "Review itinerary",
      itineraryLinkRestricted:
        "You can inspect the availability decision, but you do not have permission to open the linked itinerary.",
      actionsRestricted:
        "Availability actions are read-only for your permissions and Query scope.",
      bookingLockedTitle: "Booking Locked",
      bookingLockedAmendment:
        "The confirmed baseline cannot be changed here. Use the approved Amendment workflow for later changes.",
      bookingLockedReadOnly:
        "The confirmed baseline cannot be changed here. You do not have permission to create an Amendment.",
      readyTitle: "Ready to Invoice",
      readyDescription:
        "Backend readiness confirms {version} is cleared through {expiryDate} ({timezone}).",
      pendingTitle: "Pending Availability",
      pendingDescription:
        "Accounts may inspect this availability work. Payment schedule and Finance Gate 1 actions remain unavailable until the backend reports Ready to Invoice.",
      reviewNotes: "Review notes (optional)",
      proposalType: "Proposal type",
      proposalTypes: {
        RESEQUENCE: "Sequence only",
        ADD: "Add item",
        REMOVE: "Remove item",
        MODIFY: "Modify item",
      },
      proposalReason: "Reason",
      proposalSummary: "Proposed change summary",
      affectedItem: "Affected itinerary item",
      noAffectedItem: "No existing item selected",
      affectedItemRequired: "Required for Remove and Modify proposals.",
      affectedItemOptional: "Optional for Add proposals.",
      internalNotes: "Internal notes (optional)",
      itineraryDay: "Itinerary day",
      chooseDay: "Choose an itinerary day",
      serviceType: "Service type",
      quantity: "Quantity",
      service: "Service",
      name: "Name",
      descriptionField: "Description",
      serviceDate: "Service date",
      startTime: "Start time",
      endTime: "End time",
      hotel: "Hotel",
      roomType: "Room type",
      hotelCategory: "Hotel category",
      checkInDate: "Check-in date",
      checkOutDate: "Check-out date",
      submitToReservations: "Submit to Reservations",
      handoffSuccess:
        "Proposal handed to Reservations. Action ownership changed; the Query remains On Hold.",
      handoffState:
        "Reservations now owns this action. The Query lifecycle remains On Hold.",
      createSequenceRevision: "Create sequence revision",
      deliveryPending:
        "Delivery Pending. Open the created revision to review its authoritative delivery status.",
      reviewAndSendRevision: "Review and send revision",
      viewRevision: "View created revision",
      resequenceDescription:
        "Reorder existing non-Hotel items within their current itinerary day and stop. Item details and commercial values stay unchanged.",
      resequenceSuggestionDescription:
        "The complete existing non-Hotel order for each affected day is submitted. Day placement, stop, times, and content cannot change.",
      moveUp: "Move {item} up",
      moveDown: "Move {item} down",
      up: "Up",
      down: "Down",
      noItemsOnDay: "No items on this day.",
      resequenceSuccess: "Sequence-only revision created.",
      itineraryLoadFailed: "The accepted itinerary could not be loaded.",
      confirm: "Confirm decision",
      saving: "Saving…",
      availableSuccess: "Availability marked as available.",
      alternativeSuccess: "Alternative proposal recorded.",
      decisionFailed: "The availability decision could not be saved.",
      refreshReview:
        "The Hold or review changed. The latest state was refreshed; review it before submitting again. Your entered notes were kept.",
    },
    workflow: {
      label: "Query workflow progress",
      alternatePath: "Alternate path",
      lifecycleOutcome: "Lifecycle outcome",
      progress: "Step {current} of {total}",
    },
    responsibility: { label: "Query attention and actions" },
    edit: {
      action: "Edit Query",
      title: "Edit Query details",
      description: "Update the unlocked basic details for {number}.",
      close: "Close Query editor",
      cancel: "Cancel",
      save: "Save changes",
      saving: "Saving…",
      immutableHelp:
        "Destination country, Query and Trip numbers, ownership, status, workflow, and historical notes cannot be changed here.",
      agentSearchError:
        "Active Agents could not be searched. The current Agent remains selected.",
      success: "Query details updated.",
      error:
        "Query details could not be updated. Your entered information has been kept.",
      noLongerEditable:
        "This Query is no longer editable. The latest workspace state is being shown.",
    },
    salesPending: {
      title: "Sales Assignment Pending",
      description: "A Sales follow-up owner has not yet been assigned.",
      resolveAction: "Assign Sales owner",
      resolveTitle: "Resolve Sales assignment",
      assignmentEffect:
        "This changes only Sales follow-up ownership. Main status and Reservation ownership stay unchanged.",
      salesExecutive: "Sales Executive",
      selectExecutive: "Select a Sales Executive",
      noExecutives: "No eligible active Sales Executives are available.",
      optionsError: "Eligible Sales Executives could not be loaded.",
      success: "Sales owner assigned.",
      versionConflict:
        "This Query changed after the workspace loaded. Reload and review it before assigning.",
      inactiveExecutive:
        "The selected Sales Executive is inactive or no longer eligible.",
      genericError: "The Sales owner could not be assigned.",
    },
    notes: {
      title: "Notes & Remarks",
      latest: "Latest notes",
      countLabel: "{count} notes and remarks",
      empty: "No notes or remarks recorded yet.",
      inputPlaceholder: "Write a note or remark…",
      addAction: "Add Note",
      addingAction: "Adding…",
      cancelAction: "Cancel",
      viewAll: "View all",
      closeHistory: "Close notes history",
      historyDescription: "{count} append-only notes, newest first.",
      success: "Note added.",
      error: "Failed to add note.",
      required: "Note text cannot be empty.",
    },
    blocker: { title: "Attention required" },
    readOnly: {
      title: "Read-only Query",
      BOOKING_LOCKED:
        "The confirmed booking is locked. Changes must use an Amendment when that command is available.",
      COLD: "This Cold Query is read-only until it is reopened.",
      LIFECYCLE_TERMINAL:
        "This Query is read-only because its lifecycle has ended.",
    },
    actions: {
      markLost: "Mark Lost",
      lostDescription: "Record the active Query as Lost with a reason.",
      reopen: "Reopen Cold Query",
      reopenDescription:
        "Reopen this Cold Query with new future travel dates and a reason.",
      error: "The lifecycle action failed.",
    },
    states: {
      loadingHeader: "Loading Query header…",
      loadingWorkspace: "Loading Query workspace…",
      loadError: "Unable to load this Query workspace.",
      partialError:
        "This section could not be loaded. The rest of the Query workspace remains available.",
      empty: "No information is available for this section.",
      retry: "Retry",
    },
    inaccessible: {
      title: "Query unavailable",
      description: "This Query is unavailable or you do not have access to it.",
    },
    lostDialog: {
      title: "Record Query as Lost",
      reason: "Reason",
      reasonRequired: "A reason is required.",
      cancel: "Cancel",
      submit: "Mark Lost",
      success: "Query recorded as Lost.",
    },
    reopenDialog: {
      title: "Reopen Cold Query with future dates",
      startDate: "New travel start date",
      endDate: "New travel end date",
      reason: "Reason",
      fieldsRequired: "Future travel dates and a reason are required.",
      cancel: "Cancel",
      submit: "Reopen Cold Query",
      success: "Cold Query reopened.",
    },
    conflict: {
      title: "Newer Query data exists",
      description:
        "This Query changed after you loaded it. Reload the latest data, or review the currently displayed version before deciding what to do next.",
      reload: "Reload",
      review: "Review",
    },
  },
  ja: {
    header: {
      label: "クエリワークスペース",
      tripNumber: "旅行番号",
      bookingLocked: "予約ロック済み",
      destination: "目的地",
      agent: "エージェント",
      travelDates: "旅行日程",
      travellers: "旅行者",
      preferences: "希望条件",
      bookingType: "予約タイプ",
      workflowStage: "ワークフローステージ",
      lifecycleOutcome: "ライフサイクル結果",
    },
    back: "戻る",
    mainStatuses: {
      LIVE: "対応中",
      HOLD: "保留",
      CONVERTED: "成約済み",
      COLD: "コールド",
    },
    lifecycleStatuses: {
      ACTIVE: "有効",
      LOST: "失注",
      CANCELLED: "キャンセル済み",
      CLOSED: "クローズ済み",
    },
    bookingTypes: { FIT: "FIT", GROUP: "グループ" },
    stages: {
      RESERVATION_ACTIVE: "予約対応中",
      QUOTATION_SENT: "価格付き旅程送付済み",
      PRICED_ITINERARY_SENT: "価格付き旅程送付済み",
      AGENT_ACCEPTED: "エージェント承認済み",
      READY_TO_INVOICE: "請求準備完了",
      AWAITING_FINANCE_GATE_1: "財務ゲート1承認待ち",
      OPERATIONS_ACTIVE: "オペレーション対応中",
      AWAITING_FINANCE_GATE_2: "財務ゲート2承認待ち",
      QC_ACTIVE: "品質管理対応中",
      TRAVEL_PACK_READY: "トラベルパック準備完了",
      SUPPORT_ACTIVE: "サポート対応中",
      TRIP_COMPLETED: "旅行完了",
      AWAITING_DEPARTMENT_CLOSURE: "部門クローズ待ち",
      COLD: "コールド",
      CLOSED: "クローズ済み",
    },
    displayStages: {
      ACKNOWLEDGEMENT_PENDING: "受領確認待ち",
      ACKNOWLEDGED: "受領確認済み",
      ITINERARY_BUILDING: "旅程作成中",
      QUOTED_REQUOTED: "見積済み／再見積済み",
      FOLLOW_UP: "フォローアップ",
      ON_HOLD: "保留 / 空き状況確認",
      READY_TO_INVOICE: "請求準備完了",
      OPERATIONS: "オペレーション",
      FINANCE_APPROVED: "財務承認済み",
      FINAL_QC: "最終QC",
      TRAVEL_HANDOVER: "旅行引き渡し",
      ON_GROUND: "現地対応",
      AMENDMENT: "変更",
      DEPARTMENT_CHECKS: "部門チェック",
      CLOSED: "クローズ済み",
      COLD: "コールド",
    },
    tabs: {
      overview: "ワークフローステージ",
      communication: "コミュニケーション",
      itinerary: "旅程",
      quotations: "見積書",
      travellers: "旅行者",
      finance: "財務",
      operations: "オペレーション",
      qc: "品質管理",
      "travel-pack": "トラベルパック",
      support: "サポート",
      activity: "アクティビティ",
    },
    overview: {
      title: "ワークフローステージ",
      queryDetails: "クエリ詳細",
      contact: "エージェント連絡先",
      currency: "通貨",
      nights: "宿泊数",
      primaryTraveller: "代表旅行者",
      adults: "大人",
      children: "子供",
      age: "年齢",
      ages: "年齢",
      focTravellers: "FOC旅行者",
    },
    communication: {
      delegatedSender:
        "返信と旅程メールは、{executive} に割り当てられたメールボックスから送信されます。操作は監査履歴に記録されます。",
    },
    workflow: {
      label: "クエリの進行状況",
      alternatePath: "別経路",
      lifecycleOutcome: "ライフサイクル結果",
      progress: "ステップ {current} / {total}",
    },
    responsibility: { label: "クエリの注意事項と操作" },
    edit: {
      action: "クエリを編集",
      title: "クエリ詳細を編集",
      description: "{number} のロック前の基本情報を更新します。",
      close: "クエリエディターを閉じる",
      cancel: "キャンセル",
      save: "変更を保存",
      saving: "保存中…",
      immutableHelp:
        "目的地の国、クエリ番号・旅行番号、担当、ステータス、ワークフロー、履歴メモはここでは変更できません。",
      agentSearchError:
        "有効なエージェントを検索できませんでした。現在のエージェントは選択されたままです。",
      success: "クエリ詳細を更新しました。",
      error: "クエリ詳細を更新できませんでした。入力内容は保持されています。",
      noLongerEditable:
        "このクエリは編集できなくなりました。最新のワークスペース状態を表示しています。",
    },
    salesPending: {
      title: "営業担当者の割り当て待ち",
      description: "営業フォロー担当者がまだ割り当てられていません。",
      resolveAction: "営業担当者を割り当て",
      resolveTitle: "営業担当者の割り当てを解決",
      assignmentEffect:
        "変更されるのは営業フォロー担当者のみです。メインステータスと予約担当者は変更されません。",
      salesExecutive: "営業担当者",
      selectExecutive: "営業担当者を選択",
      noExecutives: "割り当て可能な有効な営業担当者がいません。",
      optionsError: "割り当て可能な営業担当者を読み込めませんでした。",
      success: "営業担当者を割り当てました。",
      versionConflict:
        "ワークスペースの読み込み後にクエリが変更されました。再読み込みして確認してから割り当ててください。",
      inactiveExecutive: "選択した営業担当者は無効か、割り当て対象外です。",
      genericError: "営業担当者を割り当てられませんでした。",
    },
    notes: {
      title: "メモ・備考",
      latest: "最新のメモ",
      countLabel: "メモ・備考 {count} 件",
      empty: "記録されたメモや備考はまだありません。",
      inputPlaceholder: "メモ・備考を入力…",
      addAction: "メモを追加",
      addingAction: "追加中…",
      cancelAction: "キャンセル",
      viewAll: "すべて表示",
      closeHistory: "メモ履歴を閉じる",
      historyDescription:
        "追記専用のメモ {count} 件を新しい順に表示しています。",
      success: "メモを追加しました。",
      error: "メモの追加に失敗しました。",
      required: "メモの入力は必須です。",
    },
    blocker: { title: "対応が必要です" },
    readOnly: {
      title: "読み取り専用クエリ",
      BOOKING_LOCKED:
        "確定済み予約はロックされています。利用可能な場合は変更申請を使用してください。",
      COLD: "このコールドクエリは再開されるまで読み取り専用です。",
      LIFECYCLE_TERMINAL:
        "ライフサイクルが終了しているため、このクエリは読み取り専用です。",
    },
    actions: {
      markLost: "失注として記録",
      lostDescription: "理由を付けて有効なクエリを失注として記録します。",
      reopen: "コールドクエリを再開",
      reopenDescription:
        "将来の旅行日程と理由を指定してコールドクエリを再開します。",
      error: "ライフサイクル操作に失敗しました。",
    },
    states: {
      loadingHeader: "クエリヘッダーを読み込み中…",
      loadingWorkspace: "クエリワークスペースを読み込み中…",
      loadError: "クエリワークスペースを読み込めませんでした。",
      partialError:
        "このセクションを読み込めませんでした。他のワークスペースは引き続き利用できます。",
      empty: "このセクションに表示する情報はありません。",
      retry: "再試行",
    },
    inaccessible: {
      title: "クエリを利用できません",
      description: "このクエリは利用できないか、アクセス権がありません。",
    },
    lostDialog: {
      title: "クエリを失注として記録",
      reason: "理由",
      reasonRequired: "理由は必須です。",
      cancel: "キャンセル",
      submit: "失注として記録",
      success: "クエリを失注として記録しました。",
    },
    reopenDialog: {
      title: "将来の日程でコールドクエリを再開",
      startDate: "新しい旅行開始日",
      endDate: "新しい旅行終了日",
      reason: "理由",
      fieldsRequired: "将来の旅行日程と理由は必須です。",
      cancel: "キャンセル",
      submit: "コールドクエリを再開",
      success: "コールドクエリを再開しました。",
    },
    conflict: {
      title: "新しいクエリデータがあります",
      description:
        "読み込み後にこのクエリが変更されました。最新データを再読み込みするか、現在表示中の内容を確認してください。",
      reload: "再読み込み",
      review: "確認",
    },
  },
  "zh-TW": {
    header: {
      label: "查詢工作區",
      tripNumber: "行程編號",
      bookingLocked: "預訂已鎖定",
      destination: "目的地",
      agent: "代理商",
      travelDates: "旅程日期",
      travellers: "旅客",
      preferences: "偏好設定",
      bookingType: "預訂類型",
      workflowStage: "工作流程階段",
      lifecycleOutcome: "生命週期結果",
    },
    back: "返回",
    mainStatuses: {
      LIVE: "進行中",
      HOLD: "保留",
      CONVERTED: "已轉換",
      COLD: "冷卻",
    },
    lifecycleStatuses: {
      ACTIVE: "有效",
      LOST: "已流失",
      CANCELLED: "已取消",
      CLOSED: "已結案",
    },
    bookingTypes: { FIT: "FIT", GROUP: "團體" },
    stages: {
      RESERVATION_ACTIVE: "預訂處理中",
      QUOTATION_SENT: "定價行程已送出",
      PRICED_ITINERARY_SENT: "定價行程已送出",
      AGENT_ACCEPTED: "代理商已接受",
      READY_TO_INVOICE: "可開立發票",
      AWAITING_FINANCE_GATE_1: "等待財務關卡1",
      OPERATIONS_ACTIVE: "營運處理中",
      AWAITING_FINANCE_GATE_2: "等待財務關卡2",
      QC_ACTIVE: "品質管理進行中",
      TRAVEL_PACK_READY: "旅行資料包已備妥",
      SUPPORT_ACTIVE: "支援進行中",
      TRIP_COMPLETED: "旅程已完成",
      AWAITING_DEPARTMENT_CLOSURE: "等待部門結案",
      COLD: "冷卻",
      CLOSED: "已結案",
    },
    displayStages: {
      ACKNOWLEDGEMENT_PENDING: "等待確認收悉",
      ACKNOWLEDGED: "已確認收悉",
      ITINERARY_BUILDING: "行程規劃中",
      QUOTED_REQUOTED: "已報價／重新報價",
      FOLLOW_UP: "跟進",
      ON_HOLD: "暫緩／空位確認",
      READY_TO_INVOICE: "可開立發票",
      OPERATIONS: "營運",
      FINANCE_APPROVED: "財務已核准",
      FINAL_QC: "最終品質管理",
      TRAVEL_HANDOVER: "旅程交接",
      ON_GROUND: "旅途中",
      AMENDMENT: "變更",
      DEPARTMENT_CHECKS: "部門檢查",
      CLOSED: "已結案",
      COLD: "冷卻",
    },
    tabs: {
      overview: "工作流程階段",
      communication: "通訊",
      itinerary: "行程",
      quotations: "報價",
      travellers: "旅客",
      finance: "財務",
      operations: "營運",
      qc: "品質管理",
      "travel-pack": "旅行資料包",
      support: "支援",
      activity: "活動",
    },
    overview: {
      title: "工作流程階段",
      queryDetails: "查詢詳細資料",
      contact: "代理商聯絡人",
      currency: "幣別",
      nights: "住宿晚數",
      primaryTraveller: "主要旅客",
      adults: "成人",
      children: "兒童",
      age: "年齡",
      ages: "年齡",
      focTravellers: "FOC旅客",
    },
    communication: {
      delegatedSender:
        "回覆與行程郵件會由指派給 {executive} 的信箱寄出，您的操作會記錄在稽核歷程中。",
    },
    workflow: {
      label: "查詢工作流程進度",
      alternatePath: "其他路徑",
      lifecycleOutcome: "生命週期結果",
      progress: "第 {current} 步，共 {total} 步",
    },
    responsibility: { label: "查詢提醒與操作" },
    edit: {
      action: "編輯查詢",
      title: "編輯查詢詳細資料",
      description: "更新 {number} 尚未鎖定的基本資料。",
      close: "關閉查詢編輯器",
      cancel: "取消",
      save: "儲存變更",
      saving: "儲存中…",
      immutableHelp:
        "目的地國家、查詢與行程編號、負責人、狀態、工作流程及歷史備註無法在此變更。",
      agentSearchError: "無法搜尋啟用中的代理商。目前的代理商仍維持選取。",
      success: "查詢詳細資料已更新。",
      error: "無法更新查詢詳細資料。您輸入的資料已保留。",
      noLongerEditable: "此查詢已無法編輯。目前顯示的是最新工作區狀態。",
    },
    salesPending: {
      title: "等待指派業務人員",
      description: "尚未指派業務跟進負責人。",
      resolveAction: "指派業務負責人",
      resolveTitle: "處理業務指派",
      assignmentEffect:
        "這只會變更業務跟進負責人；主要狀態與預訂負責人不會變更。",
      salesExecutive: "業務專員",
      selectExecutive: "選擇業務專員",
      noExecutives: "目前沒有符合資格且啟用中的業務專員。",
      optionsError: "無法載入符合資格的業務專員。",
      success: "已指派業務負責人。",
      versionConflict: "工作區載入後，此查詢已變更。請重新載入並檢視後再指派。",
      inactiveExecutive: "選取的業務專員已停用或不再符合資格。",
      genericError: "無法指派業務負責人。",
    },
    notes: {
      title: "意見與備註",
      latest: "最新備註",
      countLabel: "共 {count} 則意見與備註",
      empty: "目前尚未記錄任何意見或備註。",
      inputPlaceholder: "撰寫意見或備註…",
      addAction: "新增備註",
      addingAction: "新增中…",
      cancelAction: "取消",
      viewAll: "查看全部",
      closeHistory: "關閉備註紀錄",
      historyDescription: "共 {count} 則僅能追加的備註，最新的顯示在前。",
      success: "已新增備註。",
      error: "無法新增備註。",
      required: "備註內容不可為空。",
    },
    blocker: { title: "需要處理" },
    readOnly: {
      title: "唯讀查詢",
      BOOKING_LOCKED: "已確認的預訂已鎖定；功能可用時，變更必須透過修訂處理。",
      COLD: "此冷卻查詢在重新開啟前為唯讀。",
      LIFECYCLE_TERMINAL: "此查詢的生命週期已結束，因此為唯讀。",
    },
    actions: {
      markLost: "標記為流失",
      lostDescription: "輸入原因，將有效查詢記錄為流失。",
      reopen: "重新開啟冷卻查詢",
      reopenDescription: "輸入新的未來旅程日期與原因，重新開啟此冷卻查詢。",
      error: "生命週期操作失敗。",
    },
    states: {
      loadingHeader: "正在載入查詢標頭…",
      loadingWorkspace: "正在載入查詢工作區…",
      loadError: "無法載入此查詢工作區。",
      partialError: "無法載入此區段；查詢工作區的其他部分仍可使用。",
      empty: "此區段沒有可顯示的資訊。",
      retry: "重試",
    },
    inaccessible: {
      title: "無法使用查詢",
      description: "此查詢無法使用，或您沒有存取權限。",
    },
    lostDialog: {
      title: "將查詢記錄為流失",
      reason: "原因",
      reasonRequired: "必須輸入原因。",
      cancel: "取消",
      submit: "標記為流失",
      success: "查詢已記錄為流失。",
    },
    reopenDialog: {
      title: "使用未來日期重新開啟冷卻查詢",
      startDate: "新的旅程開始日期",
      endDate: "新的旅程結束日期",
      reason: "原因",
      fieldsRequired: "必須輸入未來旅程日期與原因。",
      cancel: "取消",
      submit: "重新開啟冷卻查詢",
      success: "冷卻查詢已重新開啟。",
    },
    conflict: {
      title: "有較新的查詢資料",
      description:
        "此查詢在您載入後已變更。請重新載入最新資料，或先檢視目前顯示的版本。",
      reload: "重新載入",
      review: "檢視",
    },
  },
  ko: {
    header: {
      label: "쿼리 작업 공간",
      tripNumber: "여행 번호",
      bookingLocked: "예약 잠김",
      destination: "목적지",
      agent: "에이전트",
      travelDates: "여행 일정",
      travellers: "여행자",
      preferences: "선호 사항",
      bookingType: "예약 유형",
      workflowStage: "워크플로 단계",
      lifecycleOutcome: "수명주기 결과",
    },
    back: "뒤로",
    mainStatuses: {
      LIVE: "진행 중",
      HOLD: "보류",
      CONVERTED: "전환됨",
      COLD: "콜드",
    },
    lifecycleStatuses: {
      ACTIVE: "활성",
      LOST: "실주",
      CANCELLED: "취소됨",
      CLOSED: "종료됨",
    },
    bookingTypes: { FIT: "FIT", GROUP: "그룹" },
    stages: {
      RESERVATION_ACTIVE: "예약 진행 중",
      QUOTATION_SENT: "가격 포함 여행 일정 발송됨",
      PRICED_ITINERARY_SENT: "가격 포함 여행 일정 발송됨",
      AGENT_ACCEPTED: "에이전트 승인됨",
      READY_TO_INVOICE: "송장 발행 준비 완료",
      AWAITING_FINANCE_GATE_1: "재무 게이트 1 대기",
      OPERATIONS_ACTIVE: "운영 진행 중",
      AWAITING_FINANCE_GATE_2: "재무 게이트 2 대기",
      QC_ACTIVE: "품질 관리 진행 중",
      TRAVEL_PACK_READY: "여행 자료 준비 완료",
      SUPPORT_ACTIVE: "지원 진행 중",
      TRIP_COMPLETED: "여행 완료",
      AWAITING_DEPARTMENT_CLOSURE: "부서 종료 대기",
      COLD: "콜드",
      CLOSED: "종료됨",
    },
    displayStages: {
      ACKNOWLEDGEMENT_PENDING: "접수 확인 대기",
      ACKNOWLEDGED: "접수 확인됨",
      ITINERARY_BUILDING: "일정 작성 중",
      QUOTED_REQUOTED: "견적／재견적 완료",
      FOLLOW_UP: "후속 조치",
      ON_HOLD: "보류 / 가용성 확인",
      READY_TO_INVOICE: "송장 발행 준비 완료",
      OPERATIONS: "운영",
      FINANCE_APPROVED: "재무 승인됨",
      FINAL_QC: "최종 품질 관리",
      TRAVEL_HANDOVER: "여행 인계",
      ON_GROUND: "여행 진행 중",
      AMENDMENT: "변경 요청",
      DEPARTMENT_CHECKS: "부서 점검",
      CLOSED: "종료됨",
      COLD: "콜드",
    },
    tabs: {
      overview: "워크플로 단계",
      communication: "커뮤니케이션",
      itinerary: "일정",
      quotations: "견적서",
      travellers: "여행자",
      finance: "재무",
      operations: "운영",
      qc: "품질 관리",
      "travel-pack": "여행 자료",
      support: "지원",
      activity: "활동",
    },
    overview: {
      title: "워크플로 단계",
      queryDetails: "쿼리 상세 정보",
      contact: "에이전트 연락처",
      currency: "통화",
      nights: "숙박 수",
      primaryTraveller: "대표 여행자",
      adults: "성인",
      children: "아동",
      age: "연령",
      ages: "연령",
      focTravellers: "FOC 여행자",
    },
    communication: {
      delegatedSender:
        "답장과 일정 이메일은 {executive}에게 배정된 사서함에서 전송되며, 작업자는 감사 기록에 남습니다.",
    },
    workflow: {
      label: "쿼리 워크플로 진행 상황",
      alternatePath: "대체 경로",
      lifecycleOutcome: "수명주기 결과",
      progress: "{current} / {total} 단계",
    },
    responsibility: { label: "쿼리 주의 사항 및 작업" },
    edit: {
      action: "쿼리 편집",
      title: "쿼리 상세 정보 편집",
      description: "{number}의 잠기지 않은 기본 정보를 업데이트합니다.",
      close: "쿼리 편집기 닫기",
      cancel: "취소",
      save: "변경 사항 저장",
      saving: "저장 중…",
      immutableHelp:
        "목적지 국가, 쿼리 및 여행 번호, 담당자, 상태, 워크플로와 기록 메모는 여기에서 변경할 수 없습니다.",
      agentSearchError:
        "활성 에이전트를 검색할 수 없습니다. 현재 에이전트는 계속 선택되어 있습니다.",
      success: "쿼리 상세 정보를 업데이트했습니다.",
      error:
        "쿼리 상세 정보를 업데이트할 수 없습니다. 입력한 정보는 유지됩니다.",
      noLongerEditable:
        "이 쿼리는 더 이상 편집할 수 없습니다. 최신 작업 공간 상태를 표시합니다.",
    },
    salesPending: {
      title: "영업 담당자 배정 대기",
      description: "영업 후속 담당자가 아직 배정되지 않았습니다.",
      resolveAction: "영업 담당자 배정",
      resolveTitle: "영업 담당자 배정 처리",
      assignmentEffect:
        "영업 후속 담당자만 변경됩니다. 기본 상태와 예약 담당자는 변경되지 않습니다.",
      salesExecutive: "영업 담당자",
      selectExecutive: "영업 담당자 선택",
      noExecutives: "배정 가능한 활성 영업 담당자가 없습니다.",
      optionsError: "배정 가능한 영업 담당자를 불러올 수 없습니다.",
      success: "영업 담당자를 배정했습니다.",
      versionConflict:
        "작업 공간을 불러온 후 쿼리가 변경되었습니다. 다시 불러와 검토한 뒤 배정하세요.",
      inactiveExecutive:
        "선택한 영업 담당자가 비활성 상태이거나 더 이상 자격이 없습니다.",
      genericError: "영업 담당자를 배정할 수 없습니다.",
    },
    notes: {
      title: "메모 및 비고",
      latest: "최신 메모",
      countLabel: "메모 및 비고 {count}개",
      empty: "기록된 메모나 비고가 아직 없습니다.",
      inputPlaceholder: "메모 또는 비고 작성…",
      addAction: "메모 추가",
      addingAction: "추가 중…",
      cancelAction: "취소",
      viewAll: "모두 보기",
      closeHistory: "메모 기록 닫기",
      historyDescription:
        "추가만 가능한 메모 {count}개를 최신순으로 표시합니다.",
      success: "메모가 추가되었습니다.",
      error: "메모를 추가하지 못했습니다.",
      required: "메모 내용은 필수입니다.",
    },
    blocker: { title: "조치 필요" },
    readOnly: {
      title: "읽기 전용 쿼리",
      BOOKING_LOCKED:
        "확정된 예약이 잠겼습니다. 기능이 제공되면 변경 요청을 사용해야 합니다.",
      COLD: "이 콜드 쿼리는 다시 열기 전까지 읽기 전용입니다.",
      LIFECYCLE_TERMINAL: "수명주기가 종료되어 이 쿼리는 읽기 전용입니다.",
    },
    actions: {
      markLost: "실주로 기록",
      lostDescription: "사유와 함께 활성 쿼리를 실주로 기록합니다.",
      reopen: "콜드 쿼리 다시 열기",
      reopenDescription:
        "새로운 미래 여행 날짜와 사유로 콜드 쿼리를 다시 엽니다.",
      error: "수명주기 작업에 실패했습니다.",
    },
    states: {
      loadingHeader: "쿼리 헤더 불러오는 중…",
      loadingWorkspace: "쿼리 작업 공간 불러오는 중…",
      loadError: "쿼리 작업 공간을 불러올 수 없습니다.",
      partialError:
        "이 섹션을 불러올 수 없습니다. 나머지 작업 공간은 계속 사용할 수 있습니다.",
      empty: "이 섹션에 표시할 정보가 없습니다.",
      retry: "다시 시도",
    },
    inaccessible: {
      title: "쿼리를 사용할 수 없음",
      description: "이 쿼리를 사용할 수 없거나 접근 권한이 없습니다.",
    },
    lostDialog: {
      title: "쿼리를 실주로 기록",
      reason: "사유",
      reasonRequired: "사유가 필요합니다.",
      cancel: "취소",
      submit: "실주로 기록",
      success: "쿼리를 실주로 기록했습니다.",
    },
    reopenDialog: {
      title: "미래 일정으로 콜드 쿼리 다시 열기",
      startDate: "새 여행 시작일",
      endDate: "새 여행 종료일",
      reason: "사유",
      fieldsRequired: "미래 여행 날짜와 사유가 필요합니다.",
      cancel: "취소",
      submit: "콜드 쿼리 다시 열기",
      success: "콜드 쿼리를 다시 열었습니다.",
    },
    conflict: {
      title: "새 쿼리 데이터가 있습니다",
      description:
        "불러온 후 이 쿼리가 변경되었습니다. 최신 데이터를 다시 불러오거나 현재 표시된 버전을 검토하세요.",
      reload: "다시 불러오기",
      review: "검토",
    },
  },
} as const;

export const messages = {
  en: {
    communication: communicationMessages.en,
    task10: task10Messages.en,
    followUps: followUpMessages.en,
    task11: task11Messages.en,
    ...queryFlowMessages.en,
    queryWorkspace: {
      ...queryFlowMessages.en.queryWorkspace,
      ...queryWorkspaceMessages.en,
    },
    queries: queryMessages.en,
    agents: agentMessages.en,
    itinerary: itineraryMessages.en,
    commercialTaxRules: commercialTaxRuleMessages.en,
    travelMasters: {
      ...travelMasterMessages.en,
      supplierTypes: supplierTypeMessages.en,
    },
    appName: "Travel CRM",
    foundation: "Application foundation",
    home: "Home",
    system: "System status",
    ready: "Framework ready",
    summary:
      "The application framework is running. Business modules will be added in approved vertical slices.",
    apiStatus: "API status",
    loading: "Checking system health…",
    healthy: "Healthy",
    unavailable: "Unavailable",
    retry: "Retry",
    language: "Language",
    navigation: {
      masters: "Masters",
      inCountryLocations: "In-country locations",
      hotels: "Hotels",
      suppliers: "Vendors",
      services: "Services",
      admin: "Admin",
      health: "Health",
      open: "Open navigation",
      close: "Close navigation",
      collapse: "Collapse sidebar",
      expand: "Expand sidebar",
    },
    auth: {
      signIn: "Sign in",
      signingIn: "Signing in…",
      signInDescription: "Use your Travel CRM account to continue.",
      email: "Email",
      password: "Password",
      invalidCredentials: "The email or password is incorrect.",
      genericError: "Unable to sign in. Please try again.",
      logout: "Log out",
      logoutError:
        "Logout failed. Your session may still be active. Please try again.",
    },
    confirm: {
      yes: "Yes",
      no: "No",
      cancel: "Cancel",
      confirm: "Confirm",
      deactivateTitle: "Confirm deactivation",
      deactivateMessage: "Are you sure you want to deactivate {name}?",
    },
    access: accessMessages.en,
    organization: organizationMessages.en,
    markupRules: {
      title: "Country markup rules",
      help: "Minimum must be no higher than default, and default no higher than maximum. Query markup outside these limits requires approval.",
      country: "Country",
      allCountries: "All permitted countries",
      category: "Category",
      hotel: "Hotel",
      nonHotel: "Non-Hotel",
      defaultPercentage: "Default (%)",
      minPercentage: "Minimum (%)",
      maxPercentage: "Maximum (%)",
      version: "Version",
      configuredBy: "Configured by",
      updatedAt: "Last updated",
      system: "System default",
      loading: "Loading markup rules…",
      loadFailed: "Could not load markup rules.",
      retry: "Retry",
      empty: "No markup rules found.",
      restricted: "You do not have permission to view markup rules.",
      readOnly: "You can view these rules but cannot edit them.",
      edit: "Edit markup rule",
      save: "Save",
      cancel: "Cancel",
      saved: "Markup rule updated.",
      saveFailed: "Could not save. Check the values and your permissions.",
      conflict:
        "Another user changed this rule. Review the latest values before saving. Your entered values have been preserved.",
      reviewLatest: "Use latest version and keep my entered values",
      latestValues: "Latest default / minimum / maximum",
      unavailable: "This rule is unavailable or cannot be edited.",
    },
    rateCards: {
      title: "Hotel rate cards",
      card: "Hotel rate card",
      season: "Seasons",
      rate: "Rates",
      blackout: "Blackouts",
      addCard: "Add rate card",
      add: "Add",
      addRateTo: "Add rate to {name}",
      name: "Name",
      countryId: "Country",
      hotelId: "Hotel",
      supplierId: "Supplier",
      currencyCode: "Currency",
      validFrom: "Valid from",
      validTo: "Valid to",
      validity: "Validity",
      startDate: "Start date",
      endDate: "End date",
      roomTypeId: "Room Type",
      mealPlan: "Meal plan",
      dayType: "Day type",
      reason: "Reason",
      status: "Status",
      version: "Version",
      weekendDays: "Weekend days",
      day1: "Monday",
      day2: "Tuesday",
      day3: "Wednesday",
      day4: "Thursday",
      day5: "Friday",
      day6: "Saturday",
      day7: "Sunday",
      WEEKDAY: "Weekday",
      WEEKEND: "Weekend",
      active: "Active",
      inactive: "Inactive",
      allCountries: "All countries",
      allHotels: "All Hotels",
      allSuppliers: "All Suppliers",
      allStatuses: "All statuses",
      activate: "Activate",
      deactivate: "Deactivate",
      actions: "Actions",
      remove: "Remove",
      save: "Save",
      cancel: "Cancel",
      continue: "Continue",
      yes: "Yes",
      no: "No",
      choose: "Choose an option",
      chooseSeason: "Choose a Season first.",
      previous: "Previous",
      next: "Next",
      loading: "Loading…",
      loadFailed: "Could not load rate cards.",
      lookupFailed: "Some master choices could not be loaded.",
      lookupRestricted:
        "Country, Hotel and Supplier view permissions are needed to load creation choices.",
      retry: "Retry",
      empty: "No records found.",
      restricted: "You do not have permission to view rate cards.",
      readOnly: "You can view this card but cannot edit it.",
      saved: "Rate card updated.",
      saveFailed: "Could not save the change.",
      conflict:
        "Another user changed this card. Your entries were preserved. Close and reopen the editor to review the latest values before retrying.",
      help: "Seasons and blackouts must stay within card validity. Existing Query costing is not automatically repriced.",
      confirmDeactivate: "Deactivate Hotel rate card?",
      confirmDescription:
        'Do you want to deactivate "{name}"? Existing costing history will remain available.',
      sections: {
        details: "Card Details",
        seasonsRates: "Seasons & Rates",
        blackouts: "Blackouts",
        importAccess: "Import",
      },
      context: {
        hotel: "Hotel",
        supplier: "Supplier",
        card: "Rate card",
        currency: "Currency",
        validity: "Validity",
        status: "Status",
        version: "Version",
        conflict:
          "Version conflict — review the latest values. Your entries were preserved.",
        backToHotel: "Back to Hotel",
        openHotel: "Open Hotel",
        unknownHotel: "Unknown Hotel",
        unknownSupplier: "Unknown supplier",
      },
      calendar: {
        upcoming: "Upcoming",
        current: "Current",
        expired: "Expired",
        asOf: "as of {date} ({zone})",
        utc: "UTC",
      },
      coverage: {
        title: "Validity & configuration",
        timeline: "Validity and Season timeline",
        validityRange: "Card validity: {from} – {to}",
        seasonRange: "{name}: {from} – {to}",
        blackoutRange: "{from} – {to} · {reason} ({count} days)",
        noSeasons: "No seasons",
        noRates: "No rates",
        uncoveredDates: "{count} dates outside seasons: {ranges}",
        missingCounterpart:
          "{dayType} rate missing for {room} / {meal} in {season}",
        inactiveRoomRates:
          "{count} existing rates reference {room}, which is inactive and hidden from new selections",
        configNote:
          "Configuration information, not live inventory or booking availability.",
        manualCostingNote:
          "Missing master rates do not block manual costing. A blackout suppresses automatic rate selection and requires a warned, audited manual override.",
      },
      matrix: {
        title: "Rates in {currency}",
        seasonTitle: "{name} · {from} – {to}",
        roomType: "Room Type",
        mealPlan: "Meal plan",
        weekday: "Weekday",
        weekend: "Weekend",
        missing: "Missing",
        notApplicable: "N/A",
        notApplicableWeekend:
          "No weekend day occurs in this Season under the card weekend configuration.",
        notApplicableWeekday:
          "No weekday occurs in this Season under the card weekend configuration.",
        missingHint: "No rate stored for this combination.",
        zeroHint: "A stored zero rate is a valid price, not a missing value.",
        addRate: "Add rate",
        addCombination: "Add rate combination",
        addCombinationTo: "Add rate combination to {name}",
        newRate: "New rate for {season} · {room} · {meal} · {dayType}",
        editRate: "Edit rate: {season} · {room} · {meal} · {dayType}",
        rateLabel: "{season} · {room} · {meal} · {dayType} rate in {currency}",
        saveRate: "Save rate",
        removeRate: "Remove rate",
        confirmRemoveRate: "Remove this rate?",
        confirmRemoveRateDescription:
          "Remove the {dayType} rate for {room} / {meal} in {season}? History is retained through soft removal and audit.",
        clearKeepsRate:
          "Clearing the input keeps the saved rate. Use Remove to delete it explicitly.",
        empty:
          "No rate combinations yet. Add a Season first, then add rate combinations.",
        needsSeason: "Add a Season before adding rates.",
        unsaved: "Unsaved changes",
        saved: "Rate saved.",
        saveFailed: "The rate could not be saved. Your entered value was kept.",
        suggestionsLabel:
          "Existing meal-plan labels (suggestions only; new labels are allowed)",
        inactiveRoom: "Inactive",
        roomInactiveChoices:
          "Inactive Room Types stay visible here but cannot be selected for new rates.",
      },
      seasons: {
        title: "Seasons",
        add: "Add Season",
        edit: "Edit Season",
        nameLabel: "Season name (for example LOW, PEAK)",
        remove: "Remove Season",
        confirmRemove: "Remove Season?",
        confirmRemoveDescription:
          'Remove Season "{name}" and its rates? History is retained through soft removal and audit.',
        empty:
          "No seasons yet. Seasons must sit inside card validity and must not overlap.",
      },
      blackouts: {
        add: "Add blackout",
        edit: "Edit blackout",
        empty:
          "No blackouts. Blackouts suppress automatic rate selection for their dates.",
        confirmRemove: "Remove blackout?",
        confirmRemoveDescription:
          "Remove the blackout {from} – {to}? History is retained through soft removal and audit.",
      },
      supplements: {
        title: "Supplements",
        description:
          "Mandatory supplements apply automatically when eligible and can never be silently omitted. Optional supplements need explicit selection in costing. Extra-bed supplements are per room/night. Each component is itemized in the costing breakdown.",
        empty: "No supplements configured.",
        add: "Add supplement",
        edit: "Edit supplement",
        scopeLabel: "Scope",
        scopeRequired: "Choose a scope. Entries were kept.",
        scopeProperty: "Entire Hotel",
        scopeContract: "This rate card only",
        scopeCard: "This rate card only",
        scopePropertyHelp:
          "Hotel-wide: selectable in the Hotel itinerary modal.",
        scopeContractHelp:
          "Rate-card-only: selectable by name in costing after this rate card resolves.",
        scopeCardHelp:
          "Rate-card-only: selectable by name in costing after this rate card resolves.",
        scopeNotImplied:
          "Opening this rate card never chooses a scope for you.",
        contextHotel: "Hotel: {hotel}",
        contextDirect: "Direct hotel contract · {label}",
        contextExternal: "External supplier · {label}",
        badgeProperty: "Property",
        badgeContract: "Rate card",
        badgeCard: "Rate card",
        columnScope: "Scope",
        allRoomTypes: "All room types",
        allTravellers: "All travellers",
        mandatory: "mandatory",
        optional: "optional",
        broadenTitle: "Broaden supplement to the whole Hotel?",
        broadenDescription:
          'Broaden "{name}" ({reference}) from this rate card only to the entire Hotel? It will become selectable in the Hotel itinerary modal. This choice is audited.',
        broadenConfirm: "Yes, broaden to Property",
        deactivateTitle: "Deactivate supplement?",
        deactivateDescription:
          "Deactivate {name} ({reference})? Historical snapshots keep their copied rows.",
        deactivateConfirm: "Yes, deactivate",
        deactivateReasonLabel: "Deactivation reason (required)",
        save: "Save supplement",
        cancel: "Cancel",
        requiredFields:
          "Name, reference, amount, validity dates and an explicit scope are required. Entries were kept.",
        noEditPermission:
          "You can view these supplements but cannot edit them.",
      },
      create: {
        fromHotel:
          "Creating a rate card for {hotel} ({country}). The Hotel and country are fixed to prevent mismatches.",
        fromList:
          "Creating a rate card. Hotel and country choices reset dependent selections when changed.",
        noHotel: "Select a country first, then a Hotel.",
        noSupplier: "No eligible active Supplier exists in this country.",
        createSupplier: "Create Supplier",
        noSupplierPermission:
          "Ask a user with Supplier creation permission to add one.",
        currencyHelp:
          "Three-letter ISO code. Type to search common codes; other valid codes are accepted.",
        currencyInvalid:
          "Enter a three-letter currency code (for example USD).",
        hotelMismatch:
          "The selected Hotel does not belong to the selected country.",
        headerCreated:
          "Rate-card header created. Add Seasons and Rates below — the header alone creates no usable prices.",
        chooseOption: "Choose an option",
      },
      details: {
        editTitle: "Edit card details",
        weekendHelp:
          "Weekend days use ISO weekdays. An empty selection means every date uses the Weekday rate.",
        saveDetails: "Save details",
        saved: "Card details saved.",
      },
      importAccess: {
        title: "Spreadsheet import",
        description:
          "Imports are country-scoped and validate asynchronously. Launching from this card does not limit the spreadsheet to this card.",
        open: "Open rate import",
        scopeNote:
          "A validated batch may create rates across cards in {country}. Review the batch before applying.",
      },
      unsavedGuard: {
        title: "Discard unsaved changes?",
        description:
          "You have unsaved edits on this card. Switching cards will discard them.",
        stay: "Stay",
        discard: "Discard and switch",
      },
      guideAfterCreate:
        "Header created. Add Seasons, then rate combinations, to create usable prices.",
    },
    costing: {
      title: "Costing",
      version: "Itinerary version",
      currentVersion: "Current itinerary",
      current: "Current",
      historical: "Historical — read-only",
      currency: "Selling currency",
      complete: "Complete itinerary coverage",
      incomplete: "Incomplete itinerary coverage",
      loading: "Loading costing…",
      loadFailed: "Could not load costing.",
      retry: "Retry",
      restricted: "You do not have permission to view costing.",
      readOnly: "This costing sheet is read-only.",
      notInitialized: "Costing has not been initialized for this version.",
      initialize: "Initialize current costing",
      initialized: "Costing initialized.",
      totalCost: "Total cost",
      totalMarkup: "Total Commission",
      totalSelling: "Total selling amount",
      hotelTotals: "Hotel (all combined)",
      groundTotals: "Ground activities (all combined)",
      commissionPercentage: "Commission %",
      reviewProgress: "{reviewed} of {total} reviewed",
      reviewItems: "Review {count} items",
      resolveApproval: "Resolve approval",
      ready: "Ready",
      hotelStays: "Hotel stays",
      dayContext: "Day {number} · {date} · {city}",
      editExactItinerary: "Edit this itinerary item",
      reviewReason: "Why review is required",
      previousValue: "Previous value",
      currentValue: "Current itinerary value",
      currentPricingOutcome: "Current pricing outcome",
      authoritativePreview: "Authoritative draft preview",
      previewLoading: "Refreshing calculation…",
      previewFailed:
        "The draft could not be previewed. Your saved values were not changed.",
      outOfRange:
        "This markup is outside the saved range and requires a reasoned approval.",
      markupLimits:
        "Current {current}% · Default {default}% · Allowed {min}%–{max}%",
      requestReason: "Reason for requesting out-of-range markup approval",
      requestReasonRequired: "Enter a reason for the approval request.",
      requestKeepsCurrent:
        "The active markup stays unchanged until this exact proposal is approved.",
      requestApproval: "Request approval",
      approvalRequested: "Markup approval requested.",
      pendingApproval: "Markup approval pending",
      approvalProposal: "Proposed {value}% · saved range {min}%–{max}%",
      requestedCostingVersion: "Requested against costing revision {version}",
      unknownUser: "Unknown user",
      approve: "Approve",
      reject: "Reject",
      approveRequest: "Approve markup request",
      rejectRequest: "Reject markup request",
      resolveExactProposal:
        "This resolves the retained proposal exactly. Approval applies it atomically; rejection leaves the active value unchanged.",
      resolutionReason: "Resolution reason",
      range: "Saved range: {min}%–{max}%",
      approvalApproved: "Markup request approved.",
      approvalRejected: "Markup request rejected.",
      approvalHistory: "Approval history",
      saveAndNext: "Save and review next",
      blockerGroups: {
        FIX_ITINERARY: "Fix itinerary",
        COMPLETE_COSTING: "Complete costing",
        FIX_MASTER_DATA: "Fix master data",
        RESOLVE_APPROVAL: "Resolve approval",
      },
      readOnlyReasons: {
        HISTORICAL_ITINERARY:
          "This historical itinerary and costing snapshot are read-only.",
        BOOKING_LOCKED:
          "Finance Gate 1 locked this confirmed baseline. Use an Amendment for changes.",
        QUERY_READ_ONLY: "This Query is no longer editable.",
        APPROVAL_PENDING:
          "Commercial edits are read-only while the markup request is pending.",
      },
      sourceTypes: {
        DIRECT_HOTEL: "Direct hotel contract",
        EXTERNAL_SUPPLIER: "External hotel source",
        SUPPLIER_OFFER: "Supplier offer",
        SERVICE_MASTER: "Service master",
        HOTEL_RATE_CARD: "Hotel rate card",
        MANUAL: "Manual",
      },
      sourceResolution: "Resolved because",
      blockers: "What still needs attention",
      blockerMessages: {
        ITINERARY_COVERAGE_INCOMPLETE:
          "Complete itinerary coverage for the Query travel dates.",
        HISTORICAL_ITINERARY: "This historical itinerary is read-only.",
        SELLING_CURRENCY_INVALID: "The selling currency is invalid.",
        COSTING_ITEM_COVERAGE_INCOMPLETE:
          "Every itinerary service must have a costing item.",
        COSTING_NOT_READY: "Costing is not ready.",
        ITEM_NEEDS_REVIEW: "Review the cost and selling price.",
        HOTEL_MEAL_PLAN_REQUIRED:
          "Select a meal plan to calculate the hotel price.",
        ITEM_COMMERCIAL_DATA_INCOMPLETE:
          "Complete the missing pricing details.",
        ITEM_RATE_SOURCE_INCOMPLETE: "Complete the missing pricing details.",
        ITEM_COMMERCIAL_DATA_INVALID: "Review the cost and selling price.",
        ITEM_FX_INVALID:
          "Add an FX rate to calculate the Query-currency price.",
        ITEM_DERIVED_AMOUNTS_STALE: "Review the cost and selling price.",
        MARKUP_APPROVAL_PENDING: "A markup request is waiting for approval.",
        UNKNOWN: "Review this costing issue.",
        unknown: "Review this costing issue.",
        itemContext: "Day {day} — {service}: {message}",
      },
      blockerActions: {
        openCommercial: "Review price",
        editItinerary: "Edit itinerary",
        resolveApproval: "Resolve approval",
      },
      empty: "No costing items.",
      day: "Day {number}",
      service: "Service",
      review: "Review",
      needsReview: "Needs Review",
      reviewed: "Reviewed",
      costEditRequired:
        "Cost edit permission is required before markup can be changed.",
      quantity: "Quantity",
      cost: "Cost",
      sellingPrice: "Selling price",
      commission: "Commission",
      sellingInvalid:
        "Enter a non-negative selling price with at most four decimal places.",
      sellingSaving: "Saving selling price…",
      unitCost: "Unit cost",
      costCurrency: "Cost currency",
      selectCurrency: "Select currency",
      convertedCost: "Converted cost",
      exchangeRate: "Exchange rate",
      markupPercentage: "Markup (%)",
      marginPercentage: "Margin (%)",
      selling: "Selling amount",
      itinerary: "Itinerary",
      actions: "Actions",
      rowActions: "Actions for {service}",
      openItinerary: "Open itinerary",
      edit: "Edit costing item",
      ownershipHelp:
        "Change Hotel, Supplier, Room Type, date, category and quantity in the itinerary.",
      mealPlan: "Hotel meal plan",
      rateMode: "Rate choice",
      master: "Use Master Rate",
      manual: "Manual Rate",
      fxHelp:
        "One cost-currency unit equals this many selling-currency units. Matching currencies require 1.",
      blackoutHelp:
        "A Hotel blackout requires explicit acknowledgment and a reason before a manual rate can be saved.",
      acknowledge: "I acknowledge the blackout warning",
      overrideReason: "Manual blackout override reason",
      approvalReason: "Reason for direct out-of-range markup approval",
      advancedRateHelp:
        "Master repricing uses transport metrics or seasonal activity rates from the selected Supplier offer when configured, otherwise the flat offer default.",
      pricingOutcome: "Pricing",
      breakdown: "Calculation breakdown",
      saved: "Costing updated.",
      saveFailed: "Could not save costing.",
      conflict:
        "Costing changed or became locked. Your entries were preserved. Close and reopen the editor to review current data.",
      save: "Save",
      cancel: "Cancel",
      supplements: {
        title: "Hotel supplements",
        scopeLabel: "Scope",
        itineraryTitle: "Itinerary-selected property add-ons (read-only)",
        itineraryEmpty: "No property add-ons selected in the itinerary.",
        editHotel: "Edit Hotel",
        automatic: "Applied automatically",
        selected: "Selected",
        available: "Available",
        notEligible: "Not eligible",
        notPriced: "Not priced — missing information",
        empty:
          "No commercial add-ons match this contract, Room Type and stay dates.",
        selectSourceFirst:
          "Choose a Contract / rate source to see commercial add-ons.",
        manualHidden:
          "Supplements apply only in Master Rate mode for Hotel stays.",
        savedBreakdown: "Saved calculation (previous valid snapshot)",
        savingKeepsPrevious:
          "Saving… the previous valid breakdown stays visible until the server confirms.",
        previewTitle: "Authoritative draft preview",
        previewLoading: "Refreshing preview…",
      },
      statuses: {
        DRAFT: "Draft",
        APPROVAL_PENDING: "Approval Pending",
        READY: "Ready",
      },
    },
    itineraryWorkspace: {
      contextBar: {
        loading: "Loading itinerary version…",
        loadingCosting: "Loading costing for Itinerary V{version}…",
        costingError: "Costing could not be loaded for Itinerary V{version}.",
        empty: "No itinerary version selected.",
        notInitialized: "Not initialized",
        costingLabel: "Costing",
        deliveryLocked: "Delivery Locked",
        bookingLocked: "Booking Locked",
      },
      switchGuard: {
        pending:
          "Finish the active save for Itinerary V{current} before switching to Itinerary V{target}. Your work was kept on V{current}.",
        pendingNoCurrent:
          "Finish the active save before switching versions. Your work was kept.",
        title: "Switch itinerary version?",
        description:
          "Itinerary V{current} has unsaved changes. Switching to Itinerary V{target} will discard them and clear historical quotation inspection. This cannot be undone.",
        descriptionNoCurrent:
          "There are unsaved changes. Switching versions will discard them and clear historical quotation inspection.",
        confirm: "Discard changes and switch",
        cancel: "Stay on this version",
      },
    },
    rateImports: {
      title: "Hotel rate imports",
      description:
        "Add new rates to existing cards and Seasons. Existing matching rates are rejected. Validate first, review, then Apply.",
      country: "Country",
      chooseCountry: "Choose a country",
      noCountries:
        "No active countries are available within your import scope.",
      template: "Download template",
      file: "XLSX workbook, maximum 2 MB",
      upload: "Upload and validate",
      selectFile: "Choose a country and workbook.",
      batchId: "Batch ID — keep this ID to reopen the result",
      status: "Status",
      total: "Total rows",
      valid: "Valid rows",
      invalid: "Invalid rows",
      refresh: "Refresh",
      apply: "Apply all {count} new rates",
      downloadErrors: "Download errors",
      row: "Row",
      card: "Hotel / rate card",
      season: "Season",
      room: "Room Type",
      meal: "Meal plan",
      dayType: "Day type",
      rate: "Rate",
      preview:
        "Showing {shown} of {total} valid rows. Apply includes all valid rows.",
      loading: "Loading…",
      restricted: "You do not have permission to import Hotel rates.",
      loadFailed: "Could not load import information.",
      uploadFailed:
        "Upload failed. Check the workbook or retry with the same file.",
      downloadFailed: "The download failed.",
      validationQueued: "Workbook uploaded. Validation is queued.",
      applyQueued: "Apply is queued.",
      applyFailed: "The batch could not be applied.",
      conflict:
        "The batch changed. Latest information has been loaded; review it before retrying.",
      applied: "All Hotel rates were imported.",
      failed:
        "The batch failed. Review its errors; no rates were partially imported.",
      contextNote:
        "Opened from {origin}. Imports stay country-scoped: the spreadsheet is validated as-is and may create rates across cards in {country}.",
      fromCard: "rate card {name}",
      fromHotel: "Hotel {name}",
      resumeRecent: "Resume recent batch on this device",
      noRecent: "No recent batch on this device for this country.",
      batchDiagnostics: "Batch diagnostics (technical ID)",
      batchDiagnosticsHelp:
        "The normal flow fills this in automatically. Keep the ID only for support diagnostics.",
      selectedFile: "Selected file: {name} ({size})",
      whatApplyDoes:
        "Apply will add {count} new rates from this batch. Existing matching rates are rejected, never overwritten. Nothing changes until you choose Apply.",
      cannotApply:
        "This batch cannot be applied. Only a fully valid batch (Ready to Apply) can be applied.",
      resultingCards: "Resulting rate cards",
      openCard: "Open {name}",
      refreshed: "Rate-card data refreshed.",
      validationSummary: "Validation summary",
      states: {
        UPLOADED: "Uploaded",
        VALIDATING: "Validating",
        READY_TO_APPLY: "Ready to Apply",
        APPLYING: "Applying",
        APPLIED: "Applied",
        FAILED: "Failed",
      },
    },
  },
  ja: {
    communication: communicationMessages.ja,
    task10: task10Messages.ja,
    followUps: followUpMessages.ja,
    task11: task11Messages.ja,
    ...queryFlowMessages.ja,
    queryWorkspace: {
      ...queryFlowMessages.ja.queryWorkspace,
      ...queryWorkspaceMessages.ja,
    },
    queries: queryMessages.ja,
    agents: agentMessages.ja,
    itinerary: itineraryMessages.ja,
    commercialTaxRules: commercialTaxRuleMessages.ja,
    travelMasters: {
      ...travelMasterMessages.ja,
      supplierTypes: supplierTypeMessages.ja,
    },
    appName: "トラベルCRM",
    foundation: "アプリケーション基盤",
    home: "ホーム",
    system: "システム状態",
    ready: "フレームワーク準備完了",
    summary:
      "アプリケーション基盤が稼働しています。承認された単位で業務モジュールを追加します。",
    apiStatus: "API状態",
    loading: "システム状態を確認中…",
    healthy: "正常",
    unavailable: "利用不可",
    retry: "再試行",
    language: "言語",
    navigation: {
      masters: "マスター",
      inCountryLocations: "国内ロケーション",
      hotels: "ホテル",
      suppliers: "サプライヤー",
      services: "サービス",
      admin: "管理",
      health: "ヘルス",
      open: "ナビゲーションを開く",
      close: "ナビゲーションを閉じる",
      collapse: "サイドバーを折りたたむ",
      expand: "サイドバーを展開する",
    },
    auth: {
      signIn: "サインイン",
      signingIn: "サインイン中…",
      signInDescription: "Travel CRMアカウントで続行してください。",
      email: "メールアドレス",
      password: "パスワード",
      invalidCredentials: "メールアドレスまたはパスワードが正しくありません。",
      genericError: "サインインできません。もう一度お試しください。",
      logout: "ログアウト",
      logoutError:
        "ログアウトに失敗しました。セッションが有効な可能性があります。もう一度お試しください。",
    },
    confirm: {
      yes: "はい",
      no: "いいえ",
      cancel: "キャンセル",
      confirm: "確認",
      deactivateTitle: "無効化の確認",
      deactivateMessage: "{name} を無効化してもよろしいですか？",
    },
    access: accessMessages.ja,
    organization: organizationMessages.ja,
  },
  "zh-TW": {
    communication: communicationMessages["zh-TW"],
    task10: task10Messages["zh-TW"],
    followUps: followUpMessages["zh-TW"],
    task11: task11Messages["zh-TW"],
    ...queryFlowMessages["zh-TW"],
    queryWorkspace: {
      ...queryFlowMessages["zh-TW"].queryWorkspace,
      ...queryWorkspaceMessages["zh-TW"],
    },
    queries: queryMessages["zh-TW"],
    agents: agentMessages["zh-TW"],
    itinerary: itineraryMessages["zh-TW"],
    commercialTaxRules: commercialTaxRuleMessages["zh-TW"],
    travelMasters: {
      ...travelMasterMessages["zh-TW"],
      supplierTypes: supplierTypeMessages["zh-TW"],
    },
    appName: "旅遊CRM",
    foundation: "應用程式基礎",
    home: "首頁",
    system: "系統狀態",
    ready: "框架已就緒",
    summary: "應用程式框架正在運行。業務模組將依核准的垂直切片加入。",
    apiStatus: "API狀態",
    loading: "正在檢查系統健康狀態…",
    healthy: "正常",
    unavailable: "無法使用",
    retry: "重試",
    language: "語言",
    navigation: {
      masters: "主資料",
      inCountryLocations: "境內地點",
      hotels: "飯店",
      suppliers: "供應商",
      services: "服務",
      admin: "管理",
      health: "健康狀態",
      open: "開啟導覽",
      close: "關閉導覽",
      collapse: "收合側邊欄",
      expand: "展開側邊欄",
    },
    auth: {
      signIn: "登入",
      signingIn: "登入中…",
      signInDescription: "使用您的旅遊CRM帳戶繼續。",
      email: "電子郵件",
      password: "密碼",
      invalidCredentials: "電子郵件或密碼不正確。",
      genericError: "無法登入，請再試一次。",
      logout: "登出",
      logoutError: "登出失敗，工作階段可能仍然有效。請再試一次。",
    },
    confirm: {
      yes: "是",
      no: "否",
      cancel: "取消",
      confirm: "確認",
      deactivateTitle: "確認停用",
      deactivateMessage: "您確定要停用「{name}」嗎？",
    },
    access: accessMessages["zh-TW"],
    organization: organizationMessages["zh-TW"],
  },
  ko: {
    communication: communicationMessages.ko,
    task10: task10Messages.ko,
    followUps: followUpMessages.ko,
    task11: task11Messages.ko,
    ...queryFlowMessages.ko,
    queryWorkspace: {
      ...queryFlowMessages.ko.queryWorkspace,
      ...queryWorkspaceMessages.ko,
    },
    queries: queryMessages.ko,
    agents: agentMessages.ko,
    itinerary: itineraryMessages.ko,
    commercialTaxRules: commercialTaxRuleMessages.ko,
    travelMasters: {
      ...travelMasterMessages.ko,
      supplierTypes: supplierTypeMessages.ko,
    },
    appName: "여행 CRM",
    foundation: "애플리케이션 기반",
    home: "홈",
    system: "시스템 상태",
    ready: "프레임워크 준비 완료",
    summary:
      "애플리케이션 프레임워크가 실행 중입니다. 승인된 수직 단위로 업무 모듈을 추가합니다.",
    apiStatus: "API 상태",
    loading: "시스템 상태 확인 중…",
    healthy: "정상",
    unavailable: "사용 불가",
    retry: "다시 시도",
    language: "언어",
    navigation: {
      masters: "마스터",
      inCountryLocations: "국내 위치",
      hotels: "호텔",
      suppliers: "공급업체",
      services: "서비스",
      admin: "관리",
      health: "상태 점검",
      open: "탐색 열기",
      close: "탐색 닫기",
      collapse: "사이드바 접기",
      expand: "사이드바 펼치기",
    },
    auth: {
      signIn: "로그인",
      signingIn: "로그인 중…",
      signInDescription: "Travel CRM 계정으로 계속하세요.",
      email: "이메일",
      password: "비밀번호",
      invalidCredentials: "이메일 또는 비밀번호가 올바르지 않습니다.",
      genericError: "로그인할 수 없습니다. 다시 시도해 주세요.",
      logout: "로그아웃",
      logoutError:
        "로그아웃하지 못했습니다. 세션이 아직 활성 상태일 수 있습니다. 다시 시도해 주세요.",
    },
    confirm: {
      yes: "예",
      no: "아니요",
      cancel: "취소",
      confirm: "확인",
      deactivateTitle: "비활성화 확인",
      deactivateMessage: "{name}을(를) 비활성화하시겠습니까?",
    },
    access: accessMessages.ko,
    organization: organizationMessages.ko,
  },
} as const;

export function createAppI18n(locale = "en") {
  return createI18n({ legacy: false, locale, fallbackLocale: "en", messages });
}
